import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { toast } from 'react-toastify';
import { db } from '../../firebase';
import { auth } from '../../firebase';
import {
  bundleKey,
  ensureCloudMigratedFromLocal,
  getCampaignStateRef,
  pushCampaignState,
  readLocalCache,
  migrateCampaignListItems,
  clearCampaignStateCloudAndLocal,
  writeLocalCache,
  ROWS_STORAGE_KEY,
  STAFF_STATUS_STORAGE_KEY,
  CAMPAIGN_LIST_STORAGE_KEY
} from '../../utils/campaignFirestoreSync';
import CampaignTable from './CampaignTable';
import StaffOutputModal from './StaffOutputModal';
import ManagerReportModal from './ManagerReportModal';
import StaffBranchCampaignPanel from './StaffBranchCampaignPanel';
import CampaignListModal from './CampaignListModal';
import CampaignDetailTabsModal from './CampaignDetailTabsModal';
import CampaignVisualAssetsModal from './CampaignVisualAssetsModal';
import { alignSlotIdeaNotesToImages } from './campaignVisualSlotUtils';
import {
  generateDailyCampaignPlan,
  generateManagerReport,
  generateSmartActions,
  generateStaffOutput,
  normalizeCampaignName
} from './campaignAiEngine';
import { apiUrl } from '../../utils/apiBase';

const dayName = (date) =>
  date.toLocaleDateString('tr-TR', {
    weekday: 'long'
  });

const dateStr = (date) => date.toLocaleDateString('tr-TR');
const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const getCampaignAiUrl = () => apiUrl('/api/campaign-ai');
const getCampaignAiHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  const sharedSecret = safeString(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET).trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    // Token eklenemezse backend env ayarına göre isteği reddedebilir.
  }
  return headers;
};
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MIN_VISUAL_SLOTS = 6;
const MAX_VISUAL_SLOTS = 60;
const BRANCHES = [
  { slug: 'kavakli_sube', label: 'Kavaklı Şube' },
  { slug: 'sefakoy_sube', label: 'Sefaköy Şube' },
  { slug: 'merkez_sube', label: 'Merkez Şube' }
];
const safeArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const getCampaignTaskList = (campaign) => {
  const safeCampaign = campaign && typeof campaign === 'object' ? campaign : {};
  const storeTasks = safeArray(safeCampaign.storePlan).map((text, index) => ({
    id: `store-${index}`,
    group: 'Mağaza',
    text
  }));
  const staffTasks = safeArray(safeCampaign.staffPlan).map((text, index) => ({
    id: `staff-${index}`,
    group: 'Personel',
    text
  }));
  const customTasks = safeArray(safeCampaign.customIdeas).map((text, index) => ({
    id: `custom-${index}`,
    group: 'Yeni Fikir',
    text
  }));
  const submittedTasks = safeArray(safeCampaign.submittedIdeas)
    .filter((idea) => idea?.approved)
    .map((idea, index) => ({
      id: `submitted-${safeString(idea?.id, index.toString())}`,
      group: 'Sunulan Fikir',
      text: safeString(idea?.text)
    }));
  return [...storeTasks, ...staffTasks, ...customTasks, ...submittedTasks];
};
const isCampaignTaskDone = (status, branchSlug, campaignId, taskId) =>
  !!status?.[branchSlug]?.campaignTasks?.[campaignId]?.[taskId];
const CAMPAIGN_SPECIAL_DAYS = [
  { month: 1, day: 1, name: 'Yılbaşı', type: 'Resmi tatil' },
  { month: 1, day: 10, name: 'Çalışan Gazeteciler Günü', type: 'Özel gün' },
  { month: 1, day: 15, name: 'Miraç Kandili', type: 'Dini gün' },
  { month: 1, day: 20, name: 'Şaban Ayı Başlangıcı', type: 'Dini gün' },
  { month: 1, day: 24, name: 'Tehlike Altındaki Avukatlar Günü', type: 'Özel gün' },
  { month: 2, day: 2, name: 'Berat Kandili', type: 'Dini gün' },
  { month: 2, day: 4, name: 'Dünya Kanser Günü', type: 'Farkındalık' },
  { month: 2, day: 14, name: 'Sevgililer Günü', type: 'Ticari / özel gün' },
  { month: 2, day: 19, name: 'Ramazan Başlangıcı', type: 'Dini gün' },
  { month: 2, day: 28, name: 'Sivil Savunma Günü', type: 'Özel gün' },
  { month: 3, day: 1, name: 'Marteniçka / Martenitsa Başlangıcı', type: 'Kültürel' },
  { month: 3, day: 1, endDay: 7, name: 'Yeşilay Haftası', type: 'Özel hafta' },
  { month: 3, day: 1, endDay: 7, name: 'Deprem Haftası', type: 'Farkındalık' },
  { month: 3, day: 3, name: 'Dünya Kulak ve İşitme Günü', type: 'Farkındalık' },
  { month: 3, day: 8, name: 'Dünya Kadınlar Günü', type: 'Özel / ticari gün' },
  { month: 3, day: 8, endDay: 14, name: 'Bilim ve Teknoloji Haftası', type: 'Özel hafta' },
  { month: 3, day: 12, name: 'İstiklâl Marşı’nın Kabulü', type: 'Millî gün' },
  { month: 3, day: 14, name: 'Tıp Bayramı', type: 'Meslek günü' },
  { month: 3, day: 14, name: 'Pi Günü', type: 'Sosyal medya' },
  { month: 3, day: 16, name: 'Kadir Gecesi', type: 'Dini gün' },
  { month: 3, day: 18, name: 'Çanakkale Zaferi ve Şehitleri Anma Günü', type: 'Millî gün' },
  { month: 3, day: 18, endDay: 24, name: 'Yaşlılara Saygı Haftası', type: 'Özel hafta' },
  { month: 3, day: 19, name: 'Ramazan Bayramı Arefesi', type: 'Yarım gün resmî tatil' },
  { month: 3, day: 20, name: 'Ramazan Bayramı 1. Gün', type: 'Resmî tatil' },
  { month: 3, day: 20, name: 'Dünya Mutluluk Günü', type: 'Sosyal medya' },
  { month: 3, day: 21, name: 'Ramazan Bayramı 2. Gün', type: 'Resmî tatil' },
  { month: 3, day: 21, name: 'Nevruz Bayramı', type: 'Kültürel' },
  { month: 3, day: 21, name: 'Dünya Şiir Günü', type: 'Özel gün' },
  { month: 3, day: 21, name: 'Dünya Down Sendromu Farkındalık Günü', type: 'Farkındalık' },
  { month: 3, day: 22, name: 'Ramazan Bayramı 3. Gün', type: 'Resmî tatil' },
  { month: 3, day: 22, name: 'Dünya Su Günü', type: 'Farkındalık' },
  { month: 3, day: 23, name: 'Dünya Meteoroloji Günü', type: 'Özel gün' },
  { month: 3, day: 24, name: 'Dünya Tüberküloz Günü', type: 'Farkındalık' },
  { month: 3, day: 27, name: 'Dünya Tiyatro Günü', type: 'Kültür' },
  { month: 3, day: 27, endMonth: 4, endDay: 2, name: 'Kütüphane Haftası', type: 'Özel hafta' },
  { month: 4, day: 1, name: 'Şaka Günü', type: 'Sosyal medya' },
  { month: 4, day: 1, endDay: 7, name: 'Kanserle Savaş Haftası', type: 'Farkındalık' },
  { month: 4, day: 2, name: 'Dünya Otizm Farkındalık Günü', type: 'Farkındalık' },
  { month: 4, day: 4, name: 'Dünya Sokak Hayvanları Günü', type: 'Farkındalık' },
  { month: 4, day: 5, name: 'Avukatlar Günü', type: 'Meslek günü' },
  { month: 4, day: 7, name: 'Dünya Sağlık Günü', type: 'Farkındalık' },
  { month: 4, day: 7, endDay: 13, name: 'Sağlık Haftası', type: 'Özel hafta' },
  { month: 4, day: 10, name: 'Polis Günü', type: 'Meslek günü' },
  { month: 4, day: 11, name: 'Dünya Parkinson Günü', type: 'Farkındalık' },
  { month: 4, day: 15, name: 'Dünya Sanat Günü', type: 'Kültür' },
  { month: 4, day: 15, endDay: 22, name: 'Turizm Haftası', type: 'Özel hafta' },
  { month: 4, day: 22, name: 'Dünya Günü', type: 'Farkındalık' },
  { month: 4, day: 23, name: 'Ulusal Egemenlik ve Çocuk Bayramı', type: 'Resmi tatil' },
  { month: 4, day: 23, name: 'Dünya Kitap Günü', type: 'Kültür' },
  { month: 5, day: 1, name: 'Emek ve Dayanışma Günü', type: 'Resmi tatil' },
  { month: 5, day: 3, name: 'Dünya Basın Özgürlüğü Günü', type: 'Özel gün' },
  { month: 5, day: 5, endDay: 6, name: 'Hıdırellez / Bereket Günü', type: 'Kültürel' },
  { month: 5, day: 10, name: 'Anneler Günü', type: 'Ticari / özel gün' },
  { month: 5, day: 10, endDay: 16, name: 'Engelliler Haftası', type: 'Farkındalık' },
  { month: 5, day: 12, name: 'Hemşireler Günü', type: 'Meslek günü' },
  { month: 5, day: 14, name: 'Dünya Çiftçiler Günü', type: 'Özel gün' },
  { month: 5, day: 15, name: 'Uluslararası Aile Günü', type: 'Özel gün' },
  { month: 5, day: 18, endDay: 24, name: 'Müzeler Haftası', type: 'Kültür' },
  { month: 5, day: 19, name: 'Atatürk’ü Anma, Gençlik ve Spor Bayramı', type: 'Resmi tatil' },
  { month: 5, day: 26, name: 'Kurban Bayramı Arefesi', type: 'Yarım gün resmî tatil' },
  { month: 5, day: 27, name: 'Kurban Bayramı 1. Gün', type: 'Resmî tatil' },
  { month: 5, day: 28, name: 'Kurban Bayramı 2. Gün', type: 'Resmî tatil' },
  { month: 5, day: 29, name: 'Kurban Bayramı 3. Gün', type: 'Resmî tatil' },
  { month: 5, day: 30, name: 'Kurban Bayramı 4. Gün', type: 'Resmî tatil' },
  { month: 5, day: 31, name: 'Dünya Tütünsüz Günü', type: 'Farkındalık' },
  { month: 6, day: 1, name: 'Dünya Süt Günü', type: 'Özel gün' },
  { month: 6, day: 5, name: 'Dünya Çevre Günü', type: 'Farkındalık' },
  { month: 6, day: 5, endDay: 11, name: 'Çevre Koruma Haftası', type: 'Özel hafta' },
  { month: 6, day: 12, name: 'Dünya Çocuk İşçiliğiyle Mücadele Günü', type: 'Farkındalık' },
  { month: 6, day: 14, name: 'Dünya Gönüllü Kan Bağışçıları Günü', type: 'Farkındalık' },
  { month: 6, day: 16, name: 'Hicri Yılbaşı', type: 'Dini gün' },
  { month: 6, day: 20, name: 'Dünya Mülteciler Günü', type: 'Farkındalık' },
  { month: 6, day: 21, name: 'Babalar Günü', type: 'Ticari / özel gün' },
  { month: 6, day: 21, name: 'Yaz Gündönümü / En Uzun Gün', type: 'Mevsimsel' },
  { month: 6, day: 21, name: 'Dünya Müzik Günü', type: 'Kültür' },
  { month: 6, day: 21, name: 'Dünya Yoga Günü', type: 'Sosyal medya' },
  { month: 6, day: 25, name: 'Aşure Günü', type: 'Dini / kültürel' },
  { month: 6, day: 26, name: 'Uyuşturucu Kullanımı ve Kaçakçılığı ile Mücadele Günü', type: 'Farkındalık' },
  { month: 7, day: 1, name: 'Kabotaj ve Deniz Bayramı', type: 'Millî gün' },
  { month: 7, day: 2, name: 'Dünya UFO Günü', type: 'Sosyal medya' },
  { month: 7, day: 3, name: 'Plastik Poşet Kullanmama Günü', type: 'Farkındalık' },
  { month: 7, day: 6, name: 'Dünya Öpücük Günü', type: 'Sosyal medya' },
  { month: 7, day: 7, name: 'Dünya Çikolata Günü', type: 'Sosyal medya' },
  { month: 7, day: 15, name: 'Demokrasi ve Milli Birlik Günü', type: 'Resmi tatil' },
  { month: 7, day: 17, name: 'Dünya Emoji Günü', type: 'Sosyal medya' },
  { month: 7, day: 20, name: 'Dünya Satranç Günü', type: 'Özel gün' },
  { month: 7, day: 22, name: 'Pi Yaklaşım Günü', type: 'Sosyal medya' },
  { month: 7, day: 24, name: 'Gazeteciler ve Basın Bayramı', type: 'Özel gün' },
  { month: 7, day: 28, name: 'Dünya Doğa Koruma Günü', type: 'Farkındalık' },
  { month: 7, day: 30, name: 'Dünya Arkadaşlık Günü', type: 'Özel gün' },
  { month: 8, day: 1, name: 'Dünya Web Günü', type: 'Sosyal medya' },
  { month: 8, day: 8, name: 'Uluslararası Kedi Günü', type: 'Sosyal medya' },
  { month: 8, day: 9, name: 'Kitapseverler Günü', type: 'Sosyal medya' },
  { month: 8, day: 13, name: 'Dünya Solaklar Günü', type: 'Sosyal medya' },
  { month: 8, day: 17, name: '17 Ağustos Depremi Anma Günü', type: 'Anma' },
  { month: 8, day: 19, name: 'Dünya Fotoğrafçılık Günü', type: 'Sosyal medya' },
  { month: 8, day: 24, name: 'Mevlid Kandili', type: 'Dini gün' },
  { month: 8, day: 26, name: 'Malazgirt Zaferi', type: 'Millî gün' },
  { month: 8, day: 30, name: 'Zafer Bayramı', type: 'Resmi tatil' },
  { month: 9, day: 1, name: 'Dünya Barış Günü', type: 'Özel gün' },
  { month: 9, day: 5, name: 'Uluslararası Hayırseverlik Günü', type: 'Özel gün' },
  { month: 9, day: 8, name: 'Dünya Okuma Yazma Günü', type: 'Farkındalık' },
  { month: 9, day: 12, name: 'Dünya İlk Yardım Günü', type: 'Farkındalık' },
  { month: 9, day: 19, name: 'Gaziler Günü', type: 'Millî gün' },
  { month: 9, day: 21, name: 'Dünya Alzheimer Günü', type: 'Farkındalık' },
  { month: 9, day: 21, name: 'Uluslararası Barış Günü', type: 'Özel gün' },
  { month: 9, day: 23, name: 'Sonbahar Ekinoksu', type: 'Mevsimsel' },
  { month: 9, day: 25, name: 'Dünya Eczacılar Günü', type: 'Meslek günü' },
  { month: 9, day: 27, name: 'Dünya Turizm Günü', type: 'Özel gün' },
  { month: 9, day: 29, name: 'Dünya Kalp Günü', type: 'Farkındalık' },
  { month: 10, day: 1, name: 'Dünya Kahve Günü', type: 'Sosyal medya' },
  { month: 10, day: 1, name: 'Dünya Yaşlılar Günü', type: 'Farkındalık' },
  { month: 10, day: 4, name: 'Hayvanları Koruma Günü', type: 'Farkındalık' },
  { month: 10, day: 8, name: 'Dünya Görme Günü', type: 'Farkındalık' },
  { month: 10, day: 10, name: 'Dünya Ruh Sağlığı Günü', type: 'Farkındalık' },
  { month: 10, day: 11, name: 'Dünya Kız Çocukları Günü', type: 'Farkındalık' },
  { month: 10, day: 16, name: 'Dünya Gıda Günü', type: 'Farkındalık' },
  { month: 10, day: 17, name: 'Yoksullukla Mücadele Günü', type: 'Farkındalık' },
  { month: 10, day: 24, name: 'Birleşmiş Milletler Günü', type: 'Özel gün' },
  { month: 10, day: 28, name: 'Cumhuriyet Bayramı Arifesi', type: 'Yarım gün resmî tatil' },
  { month: 10, day: 29, name: 'Cumhuriyet Bayramı', type: 'Resmi tatil' },
  { month: 10, day: 31, name: 'Dünya Tasarruf Günü', type: 'Özel gün' },
  { month: 10, day: 31, name: 'Halloween / Cadılar Bayramı', type: 'Global / sosyal medya' },
  { month: 11, day: 1, name: 'Dünya Vegan Günü', type: 'Sosyal medya' },
  { month: 11, day: 2, endDay: 8, name: 'Lösemili Çocuklar Haftası', type: 'Farkındalık' },
  { month: 11, day: 10, name: 'Atatürk’ü Anma Günü', type: 'Anma' },
  { month: 11, day: 11, name: '11.11 Bekarlar Günü', type: 'Ticari gün' },
  { month: 11, day: 11, name: 'Millî Ağaçlandırma Günü', type: 'Farkındalık' },
  { month: 11, day: 14, name: 'Dünya Diyabet Günü', type: 'Farkındalık' },
  { month: 11, day: 20, name: 'Dünya Çocuk Hakları Günü', type: 'Farkındalık' },
  { month: 11, day: 24, name: 'Öğretmenler Günü', type: 'Özel / ticari gün' },
  { month: 11, day: 25, name: 'Kadına Yönelik Şiddete Karşı Mücadele Günü', type: 'Farkındalık' },
  { month: 11, day: 27, name: 'Black Friday', type: 'Ticari gün' },
  { month: 11, day: 30, name: 'Cyber Monday', type: 'Ticari gün' },
  { month: 12, day: 1, name: 'Dünya AIDS Günü', type: 'Farkındalık' },
  { month: 12, day: 3, name: 'Dünya Engelliler Günü', type: 'Farkındalık' },
  { month: 12, day: 4, name: 'Dünya Madenciler Günü', type: 'Meslek günü' },
  { month: 12, day: 5, name: 'Dünya Kadın Hakları Günü', type: 'Özel gün' },
  { month: 12, day: 5, name: 'Dünya Türk Kahvesi Günü', type: 'Sosyal medya' },
  { month: 12, day: 5, name: 'Dünya Gönüllüler Günü', type: 'Özel gün' },
  { month: 12, day: 10, name: 'Dünya İnsan Hakları Günü', type: 'Özel gün' },
  { month: 12, day: 10, name: 'Üç Ayların Başlangıcı', type: 'Dini gün' },
  { month: 12, day: 10, name: 'Regaib Kandili', type: 'Dini gün' },
  { month: 12, day: 12, endDay: 18, name: 'Tutum, Yatırım ve Türk Malları Haftası', type: 'Özel hafta' },
  { month: 12, day: 21, name: 'Kış Gündönümü / En Uzun Gece', type: 'Mevsimsel' },
  { month: 12, day: 25, name: 'Noel', type: 'Global' },
  { month: 12, day: 31, name: 'Yılbaşı Gecesi', type: 'Ticari / özel gün' }
];
const MONTH_NAMES = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık'
];
const getYearSpecialDaysByMonth = (year = new Date().getFullYear()) =>
  MONTH_NAMES.map((monthName, index) => ({
    month: index + 1,
    monthName,
    days: CAMPAIGN_SPECIAL_DAYS
      .filter((day) => day.month === index + 1)
      .map((day) => ({
        ...day,
        date: new Date(year, day.month - 1, day.day),
        dateText: day.endDay
          ? `${day.day} ${MONTH_NAMES[day.month - 1]} - ${day.endDay} ${MONTH_NAMES[(day.endMonth || day.month) - 1]} ${year}`
          : new Date(year, day.month - 1, day.day).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }),
        dayText: new Date(year, day.month - 1, day.day).toLocaleDateString('tr-TR', { weekday: 'long' })
      }))
  }));
const getUpcomingSpecialDays = (fromDate = new Date()) => {
  const today = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  return CAMPAIGN_SPECIAL_DAYS
    .map((item) => {
      let date = new Date(today.getFullYear(), item.month - 1, item.day);
      if (date < today) date = new Date(today.getFullYear() + 1, item.month - 1, item.day);
      return {
        ...item,
        date,
        dateText: item.endDay
          ? `${item.day} ${MONTH_NAMES[item.month - 1]} - ${item.endDay} ${MONTH_NAMES[(item.endMonth || item.month) - 1]}`
          : date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }),
        dayText: date.toLocaleDateString('tr-TR', { weekday: 'long' })
      };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, 5);
};
const getSpecialDayCampaignIdea = (day) => {
  const name = safeString(day?.name, 'özel gün');
  const lower = name.toLocaleLowerCase('tr-TR');
  if (lower.includes('anneler')) return 'Anneye hediye vitrini kur. Üç fiyat seviyesinde kolye, bileklik ve küpe önerisi hazırla.';
  if (lower.includes('sevgililer')) return 'Çiftlere özel mini vitrin hazırla. Kalp, harf ve ikili kombinleri hızlı hediye alanında göster.';
  if (lower.includes('çocuk') || lower.includes('cocuk')) return 'Çocuklara uygun küçük takı ve sürpriz hediye alanı oluştur. Ailelere kolay hediye seçimi sun.';
  if (lower.includes('hıdırellez') || lower.includes('bereket')) return 'Bereket temalı altın ve şans bilekliği köşesi kur. Küçük dilek kartlarıyla mağaza ilgisini artır.';
  if (lower.includes('marteniçka') || lower.includes('martenitsa')) return 'Kırmızı-beyaz konseptli bileklik ve nazarlık alanı hazırla. Sosyal medyada kısa hikaye paylaşımı yap.';
  if (lower.includes('black friday') || lower.includes('11.11') || lower.includes('cyber')) return 'Sınırlı süreli net indirim vitrini kur. En hızlı karar verdiren ürünleri bankoda hazır göster.';
  if (lower.includes('yılbaşı') || lower.includes('yilbasi') || lower.includes('noel')) return 'Yeni yıl hediye vitrini hazırla. Parlak taşlı ürünleri paket örnekleriyle birlikte öne çıkar.';
  if (lower.includes('babalar')) return 'Babaya hediye köşesi oluştur. Erkek bileklik, yüzük ve saat uyumlu aksesuarları sade şekilde sergile.';
  return `${name} için küçük temalı vitrin hazırla. Hediye önerisi, kısa kampanya cümlesi ve bankoda hızlı seçim alanı kullan.`;
};

const toInputDate = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const parseInputDate = (value) => {
  const [year, month, day] = safeString(value).split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const parseTRDate = (value) => {
  const [day, month, year] = safeString(value).split('.').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};
const normalizeDateStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const normalizeDateEnd = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const getTimestampDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const getTransactionAmount = (islem) => {
  const savedTotal = Number(islem?.toplamTutar);
  if (Number.isFinite(savedTotal) && savedTotal > 0) return savedTotal;
  return (Number(islem?.nakit) || 0) + (Number(islem?.havaleEft) || 0) + (Number(islem?.krediKarti) || 0) + (Number(islem?.bakiye) || 0);
};
const createEmptyFocusSummary = () => ({ total: 0, count: 0, gram: 0, adet: 0, products: {} });
const createEmptySalesSummary = () => ({
  total: 0,
  count: 0,
  categories: {},
  products: {},
  jewelry: createEmptyFocusSummary(),
  fourteenKarat: createEmptyFocusSummary()
});
const getSaleProductName = (islem) => safeString(islem?.urunBilgisi, safeString(islem?.urun, 'Bilinmeyen Ürün'));
const isJewelrySale = (islem) => safeString(islem?.kategori) === 'Takı Ürün';
const isFourteenKaratSale = (islem) =>
  [islem?.urun, islem?.urunBilgisi, islem?.islemAciklamasi]
    .some((value) => safeString(value).toLocaleLowerCase('tr-TR').replace(/\s+/g, '').includes('14ayar'));
const addSaleToFocus = (focus, islem, amount) => {
  const product = getSaleProductName(islem);
  focus.total += amount;
  focus.count += 1;
  focus.gram += Number(islem?.gram) || 0;
  focus.adet += Number(islem?.adet) || 0;
  focus.products[product] = (focus.products[product] || 0) + amount;
};
const addSaleToSummary = (summary, islem) => {
  const amount = getTransactionAmount(islem);
  const category = safeString(islem?.kategori, 'Genel');
  const product = getSaleProductName(islem);
  summary.total += amount;
  summary.count += 1;
  summary.categories[category] = (summary.categories[category] || 0) + amount;
  summary.products[product] = (summary.products[product] || 0) + amount;
  if (isJewelrySale(islem)) addSaleToFocus(summary.jewelry, islem, amount);
  if (isJewelrySale(islem) && isFourteenKaratSale(islem)) addSaleToFocus(summary.fourteenKarat, islem, amount);
};
const getTopEntry = (map) => {
  const [name, value] = Object.entries(map || {}).sort((a, b) => b[1] - a[1])[0] || [];
  return name ? { name, value } : null;
};
const formatCurrency = (value) => `${Math.round(Number(value) || 0).toLocaleString('tr-TR')} TL`;
const formatGram = (value) => `${(Number(value) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} gr`;
const mergeFocusSummary = (target, source) => {
  target.total += source.total;
  target.count += source.count;
  target.gram += source.gram;
  target.adet += source.adet;
  Object.entries(source.products).forEach(([key, value]) => {
    target.products[key] = (target.products[key] || 0) + value;
  });
};
const summarizeCampaignSales = (branchResults) => {
  const before = createEmptySalesSummary();
  const during = createEmptySalesSummary();
  branchResults.forEach((branch) => {
    before.total += branch.before.total;
    before.count += branch.before.count;
    during.total += branch.during.total;
    during.count += branch.during.count;
    mergeFocusSummary(before.jewelry, branch.before.jewelry);
    mergeFocusSummary(before.fourteenKarat, branch.before.fourteenKarat);
    mergeFocusSummary(during.jewelry, branch.during.jewelry);
    mergeFocusSummary(during.fourteenKarat, branch.during.fourteenKarat);
    Object.entries(branch.during.categories).forEach(([key, value]) => {
      during.categories[key] = (during.categories[key] || 0) + value;
    });
    Object.entries(branch.during.products).forEach(([key, value]) => {
      during.products[key] = (during.products[key] || 0) + value;
    });
  });
  const diff = during.total - before.total;
  const diffRate = before.total > 0 ? Math.round((diff / before.total) * 100) : null;
  const jewelryDiff = during.jewelry.total - before.jewelry.total;
  const jewelryDiffRate = before.jewelry.total > 0 ? Math.round((jewelryDiff / before.jewelry.total) * 100) : null;
  const jewelryGramDiff = during.jewelry.gram - before.jewelry.gram;
  const jewelryGramDiffRate = before.jewelry.gram > 0 ? Math.round((jewelryGramDiff / before.jewelry.gram) * 100) : null;
  const fourteenKaratDiff = during.fourteenKarat.total - before.fourteenKarat.total;
  const fourteenKaratDiffRate = before.fourteenKarat.total > 0 ? Math.round((fourteenKaratDiff / before.fourteenKarat.total) * 100) : null;
  const fourteenKaratGramDiff = during.fourteenKarat.gram - before.fourteenKarat.gram;
  const fourteenKaratGramDiffRate = before.fourteenKarat.gram > 0 ? Math.round((fourteenKaratGramDiff / before.fourteenKarat.gram) * 100) : null;
  const topCategory = getTopEntry(during.categories);
  const topProduct = getTopEntry(during.products);
  const topJewelryProduct = getTopEntry(during.jewelry.products);
  const topFourteenKaratProduct = getTopEntry(during.fourteenKarat.products);
  return {
    before,
    during,
    diff,
    diffRate,
    jewelryDiff,
    jewelryDiffRate,
    jewelryGramDiff,
    jewelryGramDiffRate,
    fourteenKaratDiff,
    fourteenKaratDiffRate,
    fourteenKaratGramDiff,
    fourteenKaratGramDiffRate,
    topCategory,
    topProduct,
    topJewelryProduct,
    topFourteenKaratProduct,
    branchResults
  };
};

function SpecialDaysReportModal({ open, onClose }) {
  const [monthTab, setMonthTab] = useState(new Date().getMonth());
  const year = new Date().getFullYear();
  const months = useMemo(() => getYearSpecialDaysByMonth(year), [year]);
  const activeMonth = months[monthTab] || months[0];

  return (
    <Dialog className="ky-special-days-dialog" open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ className: 'ky-special-days-paper', sx: { bgcolor: '#fffaf0', color: '#2f2516', border: '1px solid rgba(151,113,39,0.24)', borderRadius: 2, height: '82vh' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(151,113,39,0.14)' }}>
        <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: '1.18rem' }}>{year} Yaklaşan Özel Günler</Typography>
        <Typography sx={{ color: '#6b5b40', fontSize: '0.86rem', mt: 0.2 }}>Ay seçerek kampanya yapılabilecek özel günleri görün.</Typography>
      </DialogTitle>
      <Tabs
        value={monthTab}
        onChange={(_, next) => setMonthTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 1,
          borderBottom: '1px solid rgba(151,113,39,0.12)',
          '& .MuiTab-root': { textTransform: 'none', color: '#6b5b40', fontWeight: 900, minHeight: 42 },
          '& .Mui-selected': { color: '#7a4d0b !important' },
          '& .MuiTabs-indicator': { bgcolor: '#9a6a1f' }
        }}
      >
        {months.map((month) => <Tab key={month.month} label={month.monthName} />)}
      </Tabs>
      <DialogContent sx={{ p: 1.5, overflow: 'auto' }}>
        <Box sx={{ display: 'grid', gap: 1 }}>
          {activeMonth.days.length ? activeMonth.days.map((day) => (
            <Box key={`${day.month}-${day.day}-${day.name}`} sx={{ border: '1px solid rgba(151,113,39,0.16)', borderRadius: 1.7, p: 1.2, bgcolor: 'rgba(255,255,255,0.76)' }}>
              <Typography sx={{ color: '#7a4d0b', fontSize: '1rem', fontWeight: 900 }}>
                {day.dateText} - {day.name}
              </Typography>
              <Typography sx={{ color: '#6b5b40', fontSize: '0.86rem', fontWeight: 800, mt: 0.2 }}>
                {day.dayText} • {day.type}
              </Typography>
              <Typography sx={{ color: '#2f2516', fontSize: '0.94rem', lineHeight: 1.45, mt: 0.6 }}>
                {getSpecialDayCampaignIdea(day)}
              </Typography>
            </Box>
          )) : (
            <Box sx={{ border: '1px solid rgba(151,113,39,0.16)', borderRadius: 1.7, p: 1.2, bgcolor: 'rgba(255,255,255,0.76)' }}>
              <Typography sx={{ color: '#7a6b55', fontSize: '0.94rem' }}>Bu ay için kayıtlı özel gün bulunmuyor.</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(151,113,39,0.14)' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#7a4d0b', fontWeight: 800 }}>Kapat</Button>
      </DialogActions>
      <style>{`
        @media (max-width: 600px) {
          .ky-special-days-paper {
            width: calc(100vw - 8px) !important;
            max-width: calc(100vw - 8px) !important;
            margin: 4px !important;
            height: calc(100svh - 8px) !important;
            max-height: calc(100svh - 8px) !important;
          }
        }
      `}</style>
    </Dialog>
  );
}

const loadStoredRows = () => readLocalCache().rows;
const loadStaffTaskStatus = () => readLocalCache().staffTaskStatus;
const loadCampaignList = () => readLocalCache().campaignList;

const createCampaignId = () => `kampanya_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const buildAiCampaignGoalText = (campaignName, goal) => {
  const safeName = safeString(campaignName, 'Yeni Kampanya');
  const safeGoal = safeString(goal, 'kampanya ürünlerini müşteriye daha net anlatmak');
  return `${safeName} için ana hedef; ${safeGoal.toLocaleLowerCase('tr-TR')} odağını mağaza, personel ve dijital kanallarda aynı dil ile uygulamak, müşteriye net fayda sunmak ve kampanya döneminde satış dönüşümünü güçlendirmektir.`;
};
const ensureTwentyStoreIdeas = (ideas, campaignName, goal) => {
  const safeName = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'müşteri ilgisini artırmak');
  const extraIdeas = [
    `${safeName} için mağaza dış camına sade ve okunur kampanya duyurusu yerleştir.`,
    'Mağaza girişine küçük karşılama standı koy; kampanyalı ürünleri ilk temas noktasında göster.',
    'Vitrinin alt bölümüne küçük hediye paketi örnekleri yerleştir; hediye algısını güçlendir.',
    'Bankoda kampanya ürünleri için ayrı kadife tepsi hazırla; ürünler dağınık görünmesin.',
    'Kasa önüne küçük sürpriz/hediye sepeti koy; alışveriş sonrası müşteriye verilecek seçenekler hazır dursun.',
    'Mağaza içinde yönlendirici küçük kartlar kullan; “hediye önerileri”, “çok tercih edilenler” gibi kısa başlıklar olsun.',
    'Vitrindeki ürünleri fiyat seviyesine göre grupla; müşteri dışarıdan bakınca seçenekleri hızlı anlasın.',
    'Mağaza dışından görünen bir ürünü kampanyanın yıldız ürünü olarak tek başına öne çıkar.',
    'Banko arkasında kampanya kutusu/paketleme alanı oluştur; paketler düzenli ve premium görünsün.',
    'Deneme alanına küçük ayna, temiz tepsi ve kombin kartı koy; müşteri ürünü üzerinde kolay hayal etsin.',
    'Girişe yakın alanda “bugünün öne çıkan ürünü” için tek ürünlük mini teşhir oluştur.',
    'Vitrinde yüksekliği farklı stantlar kullan; kampanya ürünleri düz dizilmiş gibi görünmesin.',
    'Kampanya ürünlerinin yanına küçük bakım bezi veya mini hediye kartı örneği koy.',
    'Mağaza içindeki ışığı kampanyalı ürünlere yönlendir; karanlık kalan ürünleri vitrin dışına çıkar.',
    'Bankoda hızlı paketlenmiş hazır hediye kutuları bulundur; yoğun saatte beklemeyi azalt.',
    `${safeGoal} temasını mağazada tek cümlelik masa üstü kartla görünür yap.`,
    'Mağaza çıkışında küçük teşekkür kartı ve bakım önerisi kartı verilecek alan hazırla.',
    'Kampanyalı ürünleri “günlük kullanım”, “hediye”, “özel gün” olarak fiziksel alanlara ayır.',
    'Vitrinde müşterinin dokunmak isteyeceği en parlak/parlayan ürünleri ön sıraya al.',
    'Gün sonunda vitrinde az ilgi gören ürünü değiştirip bankoda daha hızlı gösterilecek alana taşı.'
  ];
  return [...ideas, ...extraIdeas].filter((item, idx, arr) => item && arr.indexOf(item) === idx).slice(0, 20);
};
const buildStoreCampaignIdeas = (campaignName, goal) => {
  const safeName = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'müşteri ilgisini artırmak');
  const text = `${safeName} ${safeGoal}`.toLocaleLowerCase('tr-TR');

  if (text.includes('23 nisan') || text.includes('çocuk') || text.includes('cocuk')) {
    return ensureTwentyStoreIdeas([
      'Çocuklara özel takı vitrini hazırla; küçük küpe, bileklik, kolye ve renkli figürlü ürünleri ayrı sergile.',
      'Vitrine balon, kırmızı-beyaz süsleme ve çocuk bayramı temalı küçük bayrak detayları ekle.',
      'Çocuk müşterilere küçük sürpriz alanı oluştur; şeker, mini bileklik veya renkli paket kartı hazır bulunsun.',
      'Ailelere özel “çocuğa hediye” öneri köşesi kur; fiyat ve ürün seçeneklerini net ayır.',
      'Mağaza içinde çocuklarla fotoğraf çekilebilecek küçük hatıra köşesi oluştur.',
      'Bankoda “23 Nisan hediye önerileri” tepsisi hazırla; ürünler fiyat aralığına göre kolayca seçilebilsin.',
      'Çocuk ürünlerinin paketlerini renkli kurdele veya özel küçük etiketle farklılaştır.',
      'Çocuklar için mağaza çıkışında küçük sürpriz kutusu hazırla; şeker, mini bileklik veya renkli kart ekle.',
      'Vitrinde anne-baba-çocuk hediye kombinleri oluştur; aile alışverişini kolaylaştır.',
      'Gün sonunda en çok ilgi gören çocuk ürünlerini ertesi gün girişe yakın alana taşı.'
    ], safeName, safeGoal);
  }

  if (text.includes('anne')) {
    return ensureTwentyStoreIdeas([
      '“Anneye özel hediye” vitrini kur; zarif kolye, bileklik ve küpeleri yumuşak ışıkla öne çıkar.',
      'Bankoda üç bütçeli hediye tepsisi hazırla: ekonomik, özel ve premium seçenekler.',
      'Hediye paketi alanını görünür yap; kurdele, not kartı ve sertifika hazır beklesin.',
      'Vitrine anne-çocuk duygusunu anlatan küçük mesaj kartları yerleştir.',
      '“Sade anne”, “gösterişli anne” ve “klasik anne” olarak üç küçük hediye vitrini oluştur.',
      'Girişte “son dakika anne hediyesi” köşesi oluştur; hızlı karar verdiren ürünleri sergile.',
      'Set kombinleri hazırla; kolye-küpe veya bileklik-yüzük eşleştirmelerini ayrı göster.',
      'Bankoda paketlenmiş örnek hediye sunumu bulundur; müşteri alacağı paketi zihninde görsün.',
      'Vitrinde kalp, çiçek ve zarif taşlı ürünleri tek tema altında topla.',
      'Satış sonrası müşteriye küçük bakım/koruma önerisi kartı ver; hediyenin değer algısını artır.'
    ], safeName, safeGoal);
  }

  if (text.includes('sevgili') || text.includes('14 şubat') || text.includes('14 subat')) {
    return ensureTwentyStoreIdeas([
      'Çiftlere özel alyans, kolye ve bileklik vitrini kur; ikili ürünleri yan yana sergile.',
      'Bankoda “sevgiliye hızlı hediye” tepsisi hazırla; 5 net ürün önerisi bulunsun.',
      'Vitrine kırmızı detay, kalp obje ve sıcak ışıkla romantik bir alan oluştur.',
      'Hediye paketlerinde kırmızı kurdele ve küçük not kartı seçeneğini görünür yap.',
      'Mağaza girişinde günlük kullanım ve özel gün hediyesi ürünlerini iki ayrı alanda sergile.',
      'İsim/harf/kalp temalı ürünleri ayrı bir mini stantta topla.',
      'Çift kombinleri hazırla; kadın-erkek uyumlu bileklik veya kolye seçeneklerini göster.',
      'Kasa yanında son dakika hediye ürünleri için küçük premium kutu alanı kur.',
      'Vitrinde tek güçlü ürün yerine 3 farklı bütçe senaryosu oluştur.',
      'Satın alan müşteriye hediye notu yazabileceği küçük kart sun.'
    ], safeName, safeGoal);
  }

  if (text.includes('düğün') || text.includes('dugun') || text.includes('nişan') || text.includes('nisan')) {
    return ensureTwentyStoreIdeas([
      'Düğün/nişan vitrini kur; alyans, set, bilezik ve takı setlerini ayrı tema halinde sergile.',
      'Bankoda gelin-damat aileleri için hızlı danışma tepsisi hazırla.',
      'Vitrine beyaz, gold ve zarif çiçek detaylarıyla özel gün atmosferi ver.',
      '“Takı merasimi için öneriler” alanı oluştur; bilezik ve gram altın seçeneklerini görünür yap.',
      'Takı hediyesi, gelin seti ve aile alışverişi ürünlerini ayrı ayrı etiketlenmiş alanlara ayır.',
      'Premium setleri spot ışık alan bölümde sergile; fiyat/ayar bilgileri net görünsün.',
      'Aile alışverişi için oturma/danışma alanını düzenli ve hazır tut.',
      'Bankoda sertifika, kutu ve hediye paket örneklerini açık şekilde göster.',
      'Vitrinde gelin seti ve günlük kullanım setini ayrılaştır; karar vermeyi kolaylaştır.',
      'Yoğun saatlerde düğün alışverişi yapan müşteriler için hızlı ürün listesi hazır bulunsun.'
    ], safeName, safeGoal);
  }

  if (text.includes('bayram')) {
    return ensureTwentyStoreIdeas([
      'Bayram hediyesi vitrini kur; bileklik, kolye ve küçük altın seçeneklerini aile hediyesi temasıyla sergile.',
      'Bankoda “bayram ziyareti hediyesi” tepsisi hazırla; hızlı alınabilecek ürünleri öne çıkar.',
      'Vitrine sıcak ışık, sade çiçek ve bayram tebrik kartı detayı ekle.',
      'Çocuklara ve gençlere uygun küçük hediye ürünlerini ayrı mini stantta göster.',
      'Anne, genç kız ve çocuk için ayrı bayram hediyesi mini alanları oluştur.',
      'Hediye paketlerini önceden hazırla; yoğunlukta bekleme süresini azalt.',
      'Kasa yanında gram altın ve küçük takı önerilerini görünür tut.',
      'Aile kombinleri hazırla; anne, genç kız ve çocuk için ayrı öneri kartları oluştur.',
      'Bayram öncesi son gün için hızlı karar ürünlerini girişe yakın konumlandır.',
      'Gün içinde tükenen paket/malzeme ve ilgi gören ürünleri düzenli kontrol et.'
    ], safeName, safeGoal);
  }

  if (text.includes('yılbaşı') || text.includes('yilbasi')) {
    return ensureTwentyStoreIdeas([
      'Yılbaşı hediye vitrini kur; parlak taşlı, yıldız ve şans temalı ürünleri öne çıkar.',
      'Vitrine gold, kırmızı ve ışıklı küçük dekorlarla sıcak hediye atmosferi ver.',
      'Bankoda “yılbaşı için son dakika hediye” tepsisi hazırla.',
      'Hediye paketlerinde özel yılbaşı kurdelesi ve küçük not kartı seçeneği sun.',
      'Kendine hediye ve sevdiklerine hediye ürünlerini iki ayrı vitrin grubunda göster.',
      'Şans ve yeni başlangıç temalı ürünleri ayrı köşede topla.',
      'Kasa yanında küçük bütçeli hediye ürünlerini görünür hale getir.',
      'Vitrinde tek ürün değil, 3 hediye senaryosu oluştur: sade, gösterişli, premium.',
      'Günlük en çok sorulan ürünleri bankoda hazır gösterim tepsisine al.',
      'Satış sonrası paket tesliminde kısa yeni yıl dileği kartı ekle.'
    ], safeName, safeGoal);
  }

  if (text.includes('bahar') || text.includes('yaz')) {
    return ensureTwentyStoreIdeas([
      'Bahar/yaz vitrini kur; ince zincir, renkli taş, minimal bileklik ve canlı ürünleri öne çıkar.',
      'Vitrine açık ton dekor, ferah ışık ve hafif kombin hissi veren ürün grupları yerleştir.',
      'Bankoda “günlük kullanım için hafif takılar” tepsisi hazırla.',
      'Katmanlı kolye ve bileklik kombinlerini manken/stand üzerinde hazır göster.',
      'Günlük kullanım ve tatil kombinleri için iki ayrı yaz takısı alanı hazırla.',
      'Renkli taşlı ürünleri ayrı bir yaz enerjisi köşesinde sergile.',
      'Kasa yanında uygun fiyatlı küçük yaz hediyesi seçeneklerini göster.',
      'Vitrinde beyaz, mavi ve gold tonlarla ferah bir tema oluştur.',
      'Mağaza içinde yaz temalı küçük deneme/ayna köşesi hazırla; kombinler burada sergilensin.',
      'Gün sonunda en çok denenen yaz ürünlerini ertesi gün banko öneri alanına taşı.'
    ], safeName, safeGoal);
  }

  return ensureTwentyStoreIdeas([
    `${safeName} için mağaza girişinde net bir kampanya köşesi kur; müşteri içeri girince ilk bunu görsün.`,
    `Bankoda ${safeGoal} temasını anlatan 5 ürünlük hızlı öneri tepsisi hazırla.`,
    'Vitrinde kampanyanın en güçlü 3 ürününü ayrı yükselticilerle öne çıkar.',
    'Kasa yanında karar vermeyi hızlandıran küçük hediye/öneri ürünleri bulundur.',
    'Mağaza içinde kampanya avantajını anlatan küçük masa üstü bilgi kartları kullan.',
    'Ürünleri bütçeye göre ayır: giriş, orta ve premium seçenekler net görünsün.',
    'Deneme alanına kombin önerisi kartı koy; müşteri ürünü nasıl kullanacağını görsün.',
    'Hediye paketi ve sertifika örneğini bankoda görünür tut; güven algısını artır.',
    'Yoğun saatlerde hızlı gösterilecek ürünleri ayrı kutu/tepsi içinde hazır beklet.',
    'Gün sonunda ilgi gören ürünleri not alıp ertesi gün vitrin ve bankoda öne taşı.'
  ], safeName, safeGoal);
};

const buildStaffCampaignIdeas = (campaignName, goal) => {
  const safeName = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'müşteriye net fayda anlatmak');
  const text = `${safeName} ${safeGoal}`.toLocaleLowerCase('tr-TR');

  if (text.includes('23 nisan') || text.includes('çocuk') || text.includes('cocuk')) {
    return [
      'Müşteriye “Çocuğunuz için günlük kullanıma uygun, minik ve zarif hediye seçeneklerimiz var” cümlesiyle yaklaş.',
      'Aile geldiğinde önce çocuğun yaşını ve ürünü günlük mü özel gün için mi düşündüklerini sor.',
      'Çocukla göz teması kurup ürünü eğlenceli anlat; “Bu bileklik sana özel küçük bir hatıra gibi durur” gibi sıcak cümle kullan.',
      'Aileye küçük bütçeli, kolay kullanılabilir ve güvenli modelleri önce göster.',
      'Hediye paketini anlatırken “23 Nisan’a özel küçük sürpriz paket gibi hazırlayabiliriz” cümlesini kullan.',
      'Kararsız aileye 3 seçenek sun: küçük küpe, mini bileklik, zarif kolye; fazla ürünle müşteriyi yorma.',
      'Çocuk ürünü seçerken rahatlık, hafiflik ve günlük kullanım bilgisini özellikle vurgula.',
      'Anne-babaya “Hatıra olarak saklanabilecek bir hediye olsun isterseniz bu modeller daha anlamlı olur” de.',
      'Satışı kapatırken “İsterseniz yanına küçük hediye notu da ekleyelim” teklifini yap.',
      'Yoğunlukta çocuklu aileleri bekletmemek için hızlı gösterim ve hızlı paketleme taktiği uygula.'
    ];
  }

  if (text.includes('anne')) {
    return [
      'Müşteriye “Annenizin tarzı daha sade mi, gösterişli mi?” sorusuyla başlayıp ürünleri buna göre daralt.',
      'Hediye arayan müşteriye önce 3 net seçenek sun: günlük kullanım, özel gün, kalıcı hatıra.',
      '“Anneler Günü’nde en çok tercih edilen modeller bunlar” diyerek karar vermeyi kolaylaştır.',
      'Müşteriye ürünün duygusal tarafını anlat: “Bu model her taktığında sizi hatırlatacak bir hediye olur.”',
      'Kararsız müşteride bütçe sorusunu yumuşak sor: “Size uygun aralıkta birkaç güzel seçenek göstereyim.”',
      'Set ürünlerde “Kolye ve küpe birlikte daha tamamlanmış bir hediye gibi durur” cümlesini kullan.',
      'Paketleme ve not kartını satışın parçası yap: “İsterseniz hediye sunumunu özel hazırlayalım.”',
      'Ürünün ayar, kullanım ve bakım bilgisini kısa anlat; teknik detaya boğma.',
      'Son karar anında aynada/tepside ürünü tekrar gösterip “hediye olarak çok zarif duruyor” diye pekiştir.',
      'Satış kapanışında sertifika, fatura ve garanti bilgisini güven veren kısa bir dille söyle.'
    ];
  }

  if (text.includes('sevgili') || text.includes('14 şubat') || text.includes('14 subat')) {
    return [
      'Müşteriye “Hediye günlük kullanım için mi, daha özel ve romantik bir şey mi olsun?” sorusuyla yaklaş.',
      'Kararsız müşteriye kalp, harf ve zarif taşlı modellerden 3 kısa alternatif sun.',
      '“Bu model hem anlamlı hem de her gün kullanılabilecek kadar sade” cümlesiyle faydayı netleştir.',
      'Çift ürünlerinde “İsterseniz uyumlu iki parça ile daha özel bir hediye hazırlayabiliriz” önerisini yap.',
      'Hediye notu ve paket seçeneğini mutlaka söyle; romantik kampanyalarda sunum satış kararını hızlandırır.',
      'Müşteri bütçeyi söylemiyorsa “Farklı fiyat aralıklarında güzel seçeneklerimiz var” diyerek rahatlat.',
      'Ürünü gösterirken “ışıltısı güçlü ama abartılı değil” gibi net ve görsel dil kullan.',
      'Son dakika müşterisine hızlı karar vereceği hazır seçenekleri göster, uzun ürün turu yaptırma.',
      'Satışı kapatırken “Paketini özel gün hediyesi gibi hazırlayalım mı?” sorusunu kullan.',
      'Müşteri tereddüt ederse değişim/garanti/sertifika bilgisini kısa ve güven verici şekilde anlat.'
    ];
  }

  if (text.includes('düğün') || text.includes('dugun') || text.includes('nişan') || text.includes('nisan')) {
    return [
      'Müşteriye “Takı merasimi için mi, gelin/damat hediyesi için mi bakıyorsunuz?” sorusuyla doğru grubu belirle.',
      'Aile alışverişlerinde önce ihtiyacı netleştir: bilezik, set, gram altın veya özel hediye.',
      '“Bu model tören için daha gösterişli, bu model günlük kullanımda daha rahat” diye iki kullanım farkını anlat.',
      'Premium ürünlerde sertifika, ayar ve işçilik bilgisini kısa ama güven veren şekilde söyle.',
      'Kalabalık gelen ailelerde tek karar verici kişiyi nazikçe belirleyip anlatımı ona odakla.',
      'Set ürünlerde “fotoğrafta ve törende daha tamamlanmış görünür” cümlesiyle görsel etkiyi vurgula.',
      'Bütçe hassasiyetinde müşteriyi sıkıştırmadan “bu aralıkta en güçlü seçenekler bunlar” diyerek yönlendir.',
      'Kararsız müşteriye en fazla 3 ürün göster; çok seçenek düğün alışverişinde kararı zorlaştırır.',
      'Paket, kutu ve sertifika sunumunu üründen sonra mutlaka göster; değer algısını yükseltir.',
      'Satışı kapatırken teslim, paketleme ve belge kontrolünü sakin ve düzenli bir dille özetle.'
    ];
  }

  return [
    `Müşteriye kampanyayı “${safeName} kapsamında öne çıkan avantajlı ürünlerimiz var” cümlesiyle kısa tanıt.`,
    `İlk soruda müşterinin ihtiyacını netleştir: hediye mi, günlük kullanım mı, özel gün mü?`,
    'Müşteriye aynı anda çok ürün gösterme; önce 3 net seçenekle karar alanını daralt.',
    `Ürün anlatırken ${safeGoal.toLocaleLowerCase('tr-TR')} odağını tek cümleyle müşteriye bağla.`,
    'Fiyat konuşulmadan önce ürünün kullanım alanı, hediye değeri ve görünümünü anlat.',
    'Kararsız müşteriye “sade, dikkat çekici, premium” şeklinde üç farklı yol sun.',
    'Bankoda ürünü tepsi üzerinde temiz ve düzenli göster; müşterinin ürüne odaklanmasını sağla.',
    'Müşteri tereddüt ederse garanti, sertifika ve hediye paketini kısa şekilde anlatıp güven ver.',
    'Satış kapatırken “Bunu hediye paketiyle hazırlayalım mı?” gibi net bir kapanış sorusu kullan.',
    'Gün içinde en çok sorulan ürünleri not alıp sonraki müşterilerde ilk öneri olarak kullan.'
  ];
};

const buildWebsiteCampaignIdeas = (campaignName, goal) => {
  const safeName = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'kampanya ilgisini artırmak');
  return [
    `${safeName} için ana sayfada kampanyayı anlatan net bir banner alanı oluştur.`,
    'Kampanyalı ürünleri web sitesinde ayrı bir koleksiyon/kategori altında topla.',
    'Ürün kartlarında kampanya avantajını kısa bir etiketle göster: “Hediye önerisi”, “Sezon favorisi” gibi.',
    `Web açıklamalarında ${safeGoal.toLocaleLowerCase('tr-TR')} mesajını ilk paragrafta sade şekilde vurgula.`,
    'Sepete yakın alanda hediye paketi, sertifika ve güven bilgilerini görünür yap.',
    'Kampanya ürünlerinde hızlı karar için en çok tercih edilen 3 ürünü üst sıraya taşı.',
    'Mobil görünümde kampanya bannerının ilk ekranda net görünmesini sağla.',
    'Ürün detayında kullanım alanı, hediye değeri ve kombin önerisini kısa bloklar halinde göster.',
    'Kampanya süresi bitmeden “son günler” uyarısı için küçük web etiketi hazırla.',
    'Web sitesinde kampanyaya özel SEO başlığı, açıklama ve etiketleri kampanya adıyla uyumlu yap.'
  ];
};

const buildInstagramCampaignIdeas = (campaignName, goal) => {
  const safeName = safeString(campaignName, 'Kampanya');
  const safeGoal = safeString(goal, 'müşteri ilgisini artırmak');
  return [
    `${safeName} için ilk gün kısa bir duyuru postu paylaş; kampanya temasını tek görselde anlat.`,
    'Story’de 3 ürünlük “hangisini seçerdiniz?” anketi yaparak etkileşim başlat.',
    'Kampanya ürünlerini mağaza içi kısa reels videosu ile göster; vitrin, banko ve paketleme detayı görünsün.',
    `Açıklama metninde ${safeGoal.toLocaleLowerCase('tr-TR')} mesajını ilk cümlede net ver.`,
    'Her gün bir kampanya ürününü “bugünün önerisi” olarak story’de öne çıkar.',
    'Hediye paketi ve kutu sunumunu gösteren kısa video paylaş; ürünün hediye değerini artır.',
    'Müşterinin karar vermesini kolaylaştırmak için “sade / gösterişli / premium” ürün karşılaştırması paylaş.',
    'Kampanya süresince sabitlenmiş story highlight oluştur ve tüm kampanya paylaşımlarını orada topla.',
    'Son 2 gün “kampanya bitmeden” hatırlatma story’si paylaş.',
    'Mağazaya yönlendiren kısa CTA kullan: “Detaylı görmek için mağazamıza bekleriz.”'
  ];
};

function CampaignModal({ open, onClose }) {
  const skipPersistRef = useRef({ rows: false, campaigns: false });
  const stateRef = useRef({
    rows: loadStoredRows(),
    campaignList: loadCampaignList(),
    staffTaskStatus: loadStaffTaskStatus()
  });
  const skipNextCloudPushRef = useRef(false);
  const campaignSaveTimerRef = useRef(null);
  const firestoreUnsubRef = useRef(null);
  const [rows, setRows] = useState(loadStoredRows);
  const [autoInsight, setAutoInsight] = useState(null);
  const [campaignInput, setCampaignInput] = useState('');
  const [campaignList, setCampaignList] = useState(loadCampaignList);
  const [campaignListOpen, setCampaignListOpen] = useState(false);
  const [activeCampaignId, setActiveCampaignId] = useState('');
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [visualAssetsFor, setVisualAssetsFor] = useState(null);
  const [aiPlanOpen, setAiPlanOpen] = useState(false);
  const [aiPlanDraft, setAiPlanDraft] = useState(() => ({
    campaignName: '',
    startDate: toInputDate(new Date()),
    endDate: toInputDate(addDays(new Date(), 6)),
    note: ''
  }));
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [staffTaskStatus, setStaffTaskStatus] = useState(loadStaffTaskStatus);
  const [activeStaffBranch, setActiveStaffBranch] = useState(null);
  const [staffOutput, setStaffOutput] = useState(null);
  const [staffOutputModalOpen, setStaffOutputModalOpen] = useState(false);
  const [managerReport, setManagerReport] = useState(null);
  const [managerReportModalOpen, setManagerReportModalOpen] = useState(false);
  const [specialDaysReportOpen, setSpecialDaysReportOpen] = useState(false);
  const [campaignGuideOpen, setCampaignGuideOpen] = useState(false);
  const [storePerformance, setStorePerformance] = useState({
    storeName: 'Kavaklı Şube',
    salesScore: 45,
    instagramScore: 35,
    displayScore: 60,
    staffScore: 50,
    lastCampaignResult: 'low'
  });
  const [salesData, setSalesData] = useState({
    trend: 'down',
    totalSales: 80000,
    targetSales: 120000,
    averageBasket: 2500,
    conversionRate: 18,
    bestProduct: '22 Ayar Bilezik',
    weakProduct: '14 Ayar Kolye'
  });
  void setStorePerformance;
  void setSalesData;

  useLayoutEffect(() => {
    stateRef.current = { rows, campaignList, staffTaskStatus };
  }, [rows, campaignList, staffTaskStatus]);

  const validCampaignIds = useMemo(() => new Set(campaignList.map((campaign) => safeString(campaign?.id)).filter(Boolean)), [campaignList]);
  const campaignRows = useMemo(
    () => rows.filter((row) => {
      const campaignId = safeString(row?.campaignId);
      return campaignId && validCampaignIds.has(campaignId);
    }),
    [rows, validCampaignIds]
  );
  const hasActiveCampaign = Boolean(activeCampaignId && validCampaignIds.has(activeCampaignId));
  const activeCampaign = useMemo(
    () => campaignList.find((campaign) => campaign.id === activeCampaignId) || null,
    [activeCampaignId, campaignList]
  );
  const activeCampaignRows = useMemo(
    () => campaignRows.filter((row) => safeString(row?.campaignId) === activeCampaignId),
    [activeCampaignId, campaignRows]
  );
  const currentCampaignName = useMemo(() => {
    if (safeString(campaignInput).trim()) return safeString(campaignInput, 'Yeni Kampanya');
    const activeCampaignName = activeCampaign?.name;
    const firstCampaign = campaignList[0]?.name;
    return safeString(activeCampaignName, safeString(firstCampaign, 'Yeni Kampanya'));
  }, [activeCampaign, campaignInput, campaignList]);

  const clearReportState = () => {
    setAutoInsight(null);
    setStaffOutput(null);
    setManagerReport(null);
  };

  const resetCampaignRuntime = ({ removeStorage = false } = {}) => {
    if (removeStorage) {
      skipNextCloudPushRef.current = true;
      void clearCampaignStateCloudAndLocal().catch((e) => {
        console.error(e);
        toast.error('Sunucu verisi temizlenemedi.', { autoClose: 3000 });
      });
      skipPersistRef.current = { rows: true, campaigns: true };
    }
    setRows([]);
    setCampaignList([]);
    setStaffTaskStatus({});
    setActiveCampaignId('');
    setDetailCampaign(null);
    setCampaignInput('');
    clearReportState();
  };

  useEffect(() => {
    if (!open) {
      if (firestoreUnsubRef.current) {
        firestoreUnsubRef.current();
        firestoreUnsubRef.current = null;
      }
      if (campaignSaveTimerRef.current) {
        clearTimeout(campaignSaveTimerRef.current);
        campaignSaveTimerRef.current = null;
      }
      return;
    }
    let cancelled = false;
    (async () => {
      const migrated = await ensureCloudMigratedFromLocal();
      if (migrated) {
        toast.info('Kampanya verileri merkezi kayda taşındı. Giriş yapan tüm kullanıcılar aynı veriyi görür.', { autoClose: 4500 });
      }
      if (cancelled) return;
      firestoreUnsubRef.current = onSnapshot(
        getCampaignStateRef(),
        (snap) => {
          if (!snap.exists()) return;
          const d = snap.data();
          const nextRows = Array.isArray(d.rows) ? d.rows : [];
          const nextList = migrateCampaignListItems(d.campaignList);
          const nextStaff =
            d.staffTaskStatus && typeof d.staffTaskStatus === 'object' && !Array.isArray(d.staffTaskStatus) ? d.staffTaskStatus : {};
          const cur = stateRef.current;
          if (bundleKey(nextRows, nextList, nextStaff) === bundleKey(cur.rows, cur.campaignList, cur.staffTaskStatus)) {
            return;
          }
          skipNextCloudPushRef.current = true;
          setRows(nextRows);
          setCampaignList(nextList);
          setStaffTaskStatus(nextStaff);
          writeLocalCache({ rows: nextRows, campaignList: nextList, staffTaskStatus: nextStaff });
        },
        (err) => {
          console.error('Kampanya canlı veri hatası', err);
        }
      );
    })();
    return () => {
      cancelled = true;
      if (firestoreUnsubRef.current) {
        firestoreUnsubRef.current();
        firestoreUnsubRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!campaignList.length || !hasActiveCampaign) {
      setAutoInsight(null);
      return;
    }
    try {
      const autoEngineOutput = generateSmartActions({
        campaignName: currentCampaignName,
        rows: activeCampaignRows,
        storePerformance,
        salesData
      });
      setAutoInsight(autoEngineOutput);
    } catch (e) {
      setAutoInsight(null);
    }
  }, [open, campaignList.length, hasActiveCampaign, activeCampaignRows, currentCampaignName, storePerformance, salesData]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (skipNextCloudPushRef.current) {
      skipNextCloudPushRef.current = false;
      return;
    }
    if (skipPersistRef.current.rows) {
      skipPersistRef.current.rows = false;
    }
    if (skipPersistRef.current.campaigns) {
      skipPersistRef.current.campaigns = false;
    }
    try {
      localStorage.setItem(ROWS_STORAGE_KEY, JSON.stringify(rows));
      localStorage.setItem(CAMPAIGN_LIST_STORAGE_KEY, JSON.stringify(campaignList));
      localStorage.setItem(STAFF_STATUS_STORAGE_KEY, JSON.stringify(staffTaskStatus));
    } catch (e) {
      // ignore
    }
    if (campaignSaveTimerRef.current) {
      clearTimeout(campaignSaveTimerRef.current);
    }
    campaignSaveTimerRef.current = setTimeout(() => {
      pushCampaignState({ rows, campaignList, staffTaskStatus }).catch((e) => {
        console.error(e);
        toast.error(
          'Kampanya verisi UKBA (Firebase) üzerine yazılamadı. Ağ, Firestore izin kuralı veya çok büyük görsel verisi hatalı olabilir.',
          { autoClose: 6000 }
        );
      });
    }, 800);
    return () => {
      if (campaignSaveTimerRef.current) {
        clearTimeout(campaignSaveTimerRef.current);
        campaignSaveTimerRef.current = null;
      }
    };
  }, [open, rows, campaignList, staffTaskStatus]);

  const handleVisualSlotImageSet = (campaign, slotIndex, dataUrl) => {
    const id = safeString(campaign?.id);
    if (!id) return;
    const idx = Number(slotIndex);
    if (idx < 0 || idx >= MAX_VISUAL_SLOTS) return;
    let prev = Array.isArray(campaign?.visualSlotImages) ? [...campaign.visualSlotImages] : [];
    while (prev.length < MIN_VISUAL_SLOTS) prev.push(null);
    while (prev.length <= idx) prev.push(null);
    prev[idx] = dataUrl === null || dataUrl === undefined || dataUrl === '' ? null : dataUrl;
    const next = prev.slice(0, MAX_VISUAL_SLOTS);
    const nextNotes = alignSlotIdeaNotesToImages(next, campaign?.visualSlotIdeaNotes);
    setCampaignList((p) =>
      p.map((c) => (c.id === id ? { ...c, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : c))
    );
    setDetailCampaign((p) => (safeString(p?.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
    setVisualAssetsFor((p) => (p && safeString(p.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
  };

  const handleAddVisualSlot = (campaign) => {
    const id = safeString(campaign?.id);
    if (!id) return;
    let prev = Array.isArray(campaign?.visualSlotImages) ? [...campaign.visualSlotImages] : [];
    while (prev.length < MIN_VISUAL_SLOTS) prev.push(null);
    if (prev.length >= MAX_VISUAL_SLOTS) return;
    const next = [...prev, null].slice(0, MAX_VISUAL_SLOTS);
    const nextNotes = alignSlotIdeaNotesToImages(next, campaign?.visualSlotIdeaNotes);
    setCampaignList((p) => p.map((c) => (c.id === id ? { ...c, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : c)));
    setDetailCampaign((p) => (safeString(p?.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
    setVisualAssetsFor((p) => (p && safeString(p.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
    toast.success('Görsel alanı eklendi', { autoClose: 1000 });
  };

  const handleVisualSlotIdeaNoteSet = (campaign, slotIndex, text) => {
    const id = safeString(campaign?.id);
    if (!id) return;
    const idx = Number(slotIndex);
    if (idx < 0 || idx >= MAX_VISUAL_SLOTS) return;
    const imgs = (() => {
      const raw = Array.isArray(campaign?.visualSlotImages) ? [...campaign.visualSlotImages] : [];
      const r = raw.slice(0, MAX_VISUAL_SLOTS);
      while (r.length < MIN_VISUAL_SLOTS) r.push(null);
      while (r.length <= idx && r.length < MAX_VISUAL_SLOTS) r.push(null);
      return r;
    })();
    const notes = alignSlotIdeaNotesToImages(imgs, campaign?.visualSlotIdeaNotes);
    if (idx >= notes.length) return;
    const t = String(text ?? '').trim();
    const nextNotes = notes.map((n, i) => (i === idx ? (t || null) : n));
    setCampaignList((p) => p.map((c) => (c.id === id ? { ...c, visualSlotIdeaNotes: nextNotes } : c)));
    setDetailCampaign((p) => (safeString(p?.id) === id ? { ...p, visualSlotIdeaNotes: nextNotes } : p));
    setVisualAssetsFor((p) => (p && safeString(p.id) === id ? { ...p, visualSlotIdeaNotes: nextNotes } : p));
  };

  const handleRemoveExtraVisualSlot = (campaign, slotIndex) => {
    const id = safeString(campaign?.id);
    if (!id) return;
    const i = Number(slotIndex);
    let imgs = Array.isArray(campaign?.visualSlotImages) ? [...campaign.visualSlotImages] : [null, null, null];
    while (imgs.length < MIN_VISUAL_SLOTS) imgs.push(null);
    if (imgs.length <= MIN_VISUAL_SLOTS) return;
    if (i < MIN_VISUAL_SLOTS || i < 0 || i >= imgs.length) return;
    const notesAligned = alignSlotIdeaNotesToImages(imgs, campaign?.visualSlotIdeaNotes);
    imgs.splice(i, 1);
    notesAligned.splice(i, 1);
    const next = imgs.slice(0, MAX_VISUAL_SLOTS);
    const nextNotes = alignSlotIdeaNotesToImages(next, notesAligned);
    setCampaignList((p) => p.map((c) => (c.id === id ? { ...c, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : c)));
    setDetailCampaign((p) => (safeString(p?.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
    setVisualAssetsFor((p) => (p && safeString(p.id) === id ? { ...p, visualSlotImages: next, visualSlotIdeaNotes: nextNotes } : p));
    toast.success('Görsel alanı kaldırıldı', { autoClose: 1000 });
  };

  const handleGenerateAiPlan = () => {
    try {
      const finalName = normalizeCampaignName(safeString(aiPlanDraft.campaignName, currentCampaignName) || 'Yeni Kampanya');
      const start = parseInputDate(aiPlanDraft.startDate);
      const endRaw = parseInputDate(aiPlanDraft.endDate);
      const end = endRaw.getTime() >= start.getTime() ? endRaw : start;
      const campaignId = createCampaignId();
      const rawGoal = safeString(aiPlanDraft.note, 'Yönetici plan hedefi');
      const aiGoalText = buildAiCampaignGoalText(finalName, rawGoal);
      const range = { start, end, reason: rawGoal };
      const dailyPlanRaw = generateDailyCampaignPlan(finalName, range);
      const dailyPlan = Array.isArray(dailyPlanRaw)
        ? dailyPlanRaw.map((p) => ({
            tarih: safeString(p?.date, '-'),
            campaignId,
            focus: safeString(p?.campaign, finalName),
            kampanyaDetayi: safeString(aiPlanDraft.note)
              ? safeString(p?.detail, `${safeString(p?.campaign, finalName)} günlük planı - ${safeString(aiPlanDraft.note)}`)
              : safeString(p?.detail, `${safeString(p?.campaign, finalName)} günlük planı`),
            magaza: safeString(p?.magaza, '-'),
            personel: safeString(p?.personel, '-'),
            internet: safeString(p?.internet, '-'),
            instagram: safeString(p?.instagram, '-'),
            durum: safeString(p?.durum, 'Yapılacak'),
            aiSuggested: true
          }))
        : [];
      const storeIdeas = buildStoreCampaignIdeas(finalName, rawGoal);
      const staffIdeas = buildStaffCampaignIdeas(finalName, rawGoal);
      const websiteIdeas = buildWebsiteCampaignIdeas(finalName, rawGoal);
      const instagramIdeas = buildInstagramCampaignIdeas(finalName, rawGoal);
      const campaignRecord = {
        id: campaignId,
        name: finalName,
        start: dateStr(start),
        end: dateStr(end),
        createdAt: new Date().toLocaleString('tr-TR'),
        goal: rawGoal,
        campaignDetails: {
          goal: rawGoal,
          aiGoalText
        },
        storeIdeas,
        storePlan: [],
        staffIdeas,
        staffPlan: [],
        websiteIdeas,
        websitePlan: [],
        internetPlan: [],
        instagramIdeas,
        instagramPlan: [],
        customIdeas: [],
        submittedIdeas: [],
        ideaSelectionsSaved: false,
        visualSlotImages: [null, null, null, null, null, null],
        visualSlotIdeaNotes: [null, null, null]
      };
      setCampaignList((prev) => [campaignRecord, ...prev]);
      setActiveCampaignId(campaignId);
      setDetailCampaign(campaignRecord);
      handleApplyFullPlan({ campaignName: finalName, campaignId, dailyPlan, range });
      setCampaignInput(finalName);
      setAiPlanOpen(false);
      toast.success('AI plan tabloya uygulandı', { autoClose: 1600 });
    } catch (e) {
      toast.error('AI plan oluşturulamadı', { autoClose: 1800 });
    }
  };

  const handleSelectCampaign = (campaign) => {
    const campaignId = safeString(campaign?.id);
    setActiveCampaignId(campaignId);
    setCampaignInput(safeString(campaign?.name, 'Yeni Kampanya'));
  };

  const handleOpenCampaignDetail = (campaign) => {
    const campaignId = safeString(campaign?.id);
    setActiveCampaignId(campaignId);
    setCampaignInput(safeString(campaign?.name, 'Yeni Kampanya'));
    setDetailCampaign(campaign);
    setCampaignListOpen(false);
  };

  const handleOpenVisualAssets = (c) => {
    const id = safeString(c?.id);
    if (!id) return;
    const latest = campaignList.find((x) => x.id === id) || c;
    setVisualAssetsFor(latest);
  };

  const handleDeleteCampaign = (campaign) => {
    const campaignId = safeString(campaign?.id);
    if (!campaignId) return;
    const ok = window.confirm('Kampanyayı silmek istediğinize emin misiniz?');
    if (!ok) return;

    const datesToClean = new Set(rows.filter((row) => row.campaignId === campaignId).map((row) => row.tarih));
    const remainingCampaigns = campaignList.filter((item) => item.id !== campaignId);
    if (!remainingCampaigns.length) {
      resetCampaignRuntime({ removeStorage: true });
      setCampaignListOpen(false);
      toast.success('Kampanya silindi', { autoClose: 1600 });
      return;
    }
    setRows((prev) => prev.filter((row) => row.campaignId !== campaignId));
    setCampaignList(remainingCampaigns);
    setStaffTaskStatus((prev) => {
      const next = { ...prev };
      BRANCHES.forEach((branch) => {
        const branchStatus = { ...(next[branch.slug] || {}) };
        datesToClean.forEach((dateKey) => {
          delete branchStatus[dateKey];
        });
        next[branch.slug] = branchStatus;
      });
      return next;
    });
    if (activeCampaignId === campaignId) {
      setActiveCampaignId('');
      clearReportState();
    }
    toast.success('Kampanya silindi', { autoClose: 1600 });
  };

  const handleClearAllCampaignData = () => {
    const ok = window.confirm('Tüm kampanya planları ve personel görev işaretleri silinecek. Emin misiniz?');
    if (!ok) return;
    resetCampaignRuntime({ removeStorage: true });
    setCampaignListOpen(false);
    toast.success('Tüm kampanya verileri temizlendi', { autoClose: 1600 });
  };

  const handleSaveCampaignDetail = (updatedCampaign) => {
    const campaignId = safeString(updatedCampaign?.id);
    if (!campaignId) return;
    setCampaignList((prev) =>
      prev.map((campaign) => {
        if (campaign.id !== campaignId) return campaign;
        return {
          ...campaign,
          ...updatedCampaign,
          visualSlotImages:
            updatedCampaign.visualSlotImages !== undefined ? updatedCampaign.visualSlotImages : campaign.visualSlotImages,
          visualSlotIdeaNotes:
            updatedCampaign.visualSlotIdeaNotes !== undefined
              ? updatedCampaign.visualSlotIdeaNotes
              : campaign.visualSlotIdeaNotes
        };
      })
    );
    setDetailCampaign((prev) => {
      if (safeString(prev?.id) !== campaignId) return prev;
      return {
        ...prev,
        ...updatedCampaign,
        visualSlotImages:
          updatedCampaign.visualSlotImages !== undefined ? updatedCampaign.visualSlotImages : prev.visualSlotImages,
        visualSlotIdeaNotes:
          updatedCampaign.visualSlotIdeaNotes !== undefined
            ? updatedCampaign.visualSlotIdeaNotes
            : prev.visualSlotIdeaNotes
      };
    });
    toast.success('Kampanya güncellendi', { autoClose: 1400 });
  };

  const handleCampaignUpdateFromBranch = (updatedCampaign) => {
    const campaignId = safeString(updatedCampaign?.id);
    if (!campaignId) return;
    setCampaignList((prev) =>
      prev.map((campaign) => {
        if (campaign.id !== campaignId) return campaign;
        return {
          ...campaign,
          ...updatedCampaign,
          visualSlotImages:
            updatedCampaign.visualSlotImages !== undefined ? updatedCampaign.visualSlotImages : campaign.visualSlotImages,
          visualSlotIdeaNotes:
            updatedCampaign.visualSlotIdeaNotes !== undefined
              ? updatedCampaign.visualSlotIdeaNotes
              : campaign.visualSlotIdeaNotes
        };
      })
    );
    setDetailCampaign((prev) => {
      if (safeString(prev?.id) !== campaignId) return prev;
      return {
        ...prev,
        ...updatedCampaign,
        visualSlotImages:
          updatedCampaign.visualSlotImages !== undefined ? updatedCampaign.visualSlotImages : prev.visualSlotImages,
        visualSlotIdeaNotes:
          updatedCampaign.visualSlotIdeaNotes !== undefined
            ? updatedCampaign.visualSlotIdeaNotes
            : prev.visualSlotIdeaNotes
      };
    });
  };

  const handleApplyFullPlan = (engineOutput) => {
    const plan = engineOutput?.dailyPlan || [];
    if (!plan.length) return;

    setRows((all) => {
      const rowMap = new Map(all.map((row) => [row.tarih, row]));
      plan.forEach((dayPlan) => {
        const date = parseTRDate(dayPlan.tarih);
        const baseRow = rowMap.get(dayPlan.tarih) || {
          id: date ? `cmp-${toInputDate(date)}` : `cmp-${Date.now()}-${dayPlan.tarih}`,
          date: dayPlan.tarih,
          tarih: dayPlan.tarih,
          gun: date ? dayName(date).toUpperCase() : '',
          campaignId: '',
          kampanyaAdi: '',
          kampanyaDetayi: '',
          magaza: '',
          personel: '',
          internet: '',
          instagram: '',
          durum: 'Yapılacak',
          aiSuggested: false
        };
        rowMap.set(dayPlan.tarih, {
          ...baseRow,
          date: dayPlan.tarih,
          campaignId: safeString(dayPlan.campaignId, safeString(engineOutput?.campaignId, baseRow.campaignId)),
          kampanyaAdi: dayPlan.focus,
          kampanyaDetayi: dayPlan.kampanyaDetayi,
          magaza: dayPlan.magaza,
          personel: dayPlan.personel,
          internet: dayPlan.internet,
          instagram: dayPlan.instagram,
          durum: dayPlan.durum || 'Yapılacak',
          aiSuggested: !!dayPlan.aiSuggested
        });
      });
      return Array.from(rowMap.values()).sort((a, b) => {
        const da = parseTRDate(a.tarih);
        const db = parseTRDate(b.tarih);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      });
    });
  };

  const handleCreateStaffOutput = () => {
    if (!hasActiveCampaign) {
      setStaffOutput(null);
      setStaffOutputModalOpen(true);
      return;
    }
    const output = generateStaffOutput({
      campaignName: currentCampaignName,
      rows: activeCampaignRows,
      storePerformance,
      salesData,
      campaign: activeCampaign
    });
    setStaffOutput(output);
    setStaffOutputModalOpen(true);
  };

  const fetchCampaignSalesAnalysis = async (campaign) => {
    const start = parseTRDate(campaign?.start);
    const end = parseTRDate(campaign?.end);
    if (!start || !end) {
      return { available: false, message: 'Kampanya tarih aralığı okunamadı.' };
    }

    const campaignStart = normalizeDateStart(start);
    const campaignEnd = normalizeDateEnd(end);
    const days = Math.max(1, Math.ceil((campaignEnd.getTime() - campaignStart.getTime()) / MS_PER_DAY));
    const beforeEnd = new Date(campaignStart.getTime() - 1);
    const beforeStart = normalizeDateStart(new Date(campaignStart.getTime() - days * MS_PER_DAY));
    const lastWeekEnd = normalizeDateEnd(new Date());
    const lastWeekStart = normalizeDateStart(new Date(lastWeekEnd.getTime() - 6 * MS_PER_DAY));

    try {
      const lastWeek = createEmptySalesSummary();
      const branchResults = await Promise.all(
        BRANCHES.map(async (branch) => {
          const before = createEmptySalesSummary();
          const during = createEmptySalesSummary();
          const snapshot = await getDocs(query(collection(db, 'subeler', branch.slug, 'islemler'), orderBy('timestamp', 'desc')));
          snapshot.docs.forEach((docSnap) => {
            const islem = docSnap.data();
            if (safeString(islem?.islemTuru) !== 'Satış') return;
            const date = getTimestampDate(islem?.timestamp);
            if (!date) return;
            if (date >= campaignStart && date <= campaignEnd) addSaleToSummary(during, islem);
            if (date >= beforeStart && date <= beforeEnd) addSaleToSummary(before, islem);
            if (date >= lastWeekStart && date <= lastWeekEnd) addSaleToSummary(lastWeek, islem);
          });
          const diff = during.jewelry.total - before.jewelry.total;
          const diffRate = before.jewelry.total > 0 ? Math.round((diff / before.jewelry.total) * 100) : null;
          const gramDiff = during.jewelry.gram - before.jewelry.gram;
          const gramDiffRate = before.jewelry.gram > 0 ? Math.round((gramDiff / before.jewelry.gram) * 100) : null;
          const fourteenKaratDiff = during.fourteenKarat.total - before.fourteenKarat.total;
          const fourteenKaratDiffRate = before.fourteenKarat.total > 0 ? Math.round((fourteenKaratDiff / before.fourteenKarat.total) * 100) : null;
          const fourteenKaratGramDiff = during.fourteenKarat.gram - before.fourteenKarat.gram;
          const fourteenKaratGramDiffRate = before.fourteenKarat.gram > 0 ? Math.round((fourteenKaratGramDiff / before.fourteenKarat.gram) * 100) : null;
          return { ...branch, before, during, diff, diffRate, gramDiff, gramDiffRate, fourteenKaratDiff, fourteenKaratDiffRate, fourteenKaratGramDiff, fourteenKaratGramDiffRate };
        })
      );

      return {
        available: true,
        periodText: `${campaign.start} - ${campaign.end}`,
        previousPeriodText: `${dateStr(beforeStart)} - ${dateStr(beforeEnd)}`,
        campaignDays: days,
        lastWeekPeriodText: `${dateStr(lastWeekStart)} - ${dateStr(lastWeekEnd)}`,
        lastWeek,
        ...summarizeCampaignSales(branchResults)
      };
    } catch (error) {
      return { available: false, message: 'Gerçek satış verisi okunamadı. Firebase erişimi veya yetki kontrol edilmeli.' };
    }
  };

  const requestCampaignSalesAiComment = async (campaign, analysis) => {
    if (!analysis?.available) return '';
    const branchText = safeArray(analysis.branchResults)
      .map((branch) => `${branch.label}: Takı Ürün kampanya ${formatCurrency(branch.during?.jewelry?.total)} / ${formatGram(branch.during?.jewelry?.gram)}, önceki ${formatCurrency(branch.before?.jewelry?.total)} / ${formatGram(branch.before?.jewelry?.gram)}, fark ${formatCurrency(branch.diff)} / ${formatGram(branch.gramDiff)}`)
      .join(' | ');
    const jewelryProductText = analysis.topJewelryProduct ? `${analysis.topJewelryProduct.name}: ${formatCurrency(analysis.topJewelryProduct.value)}` : 'Takı Ürün ürün verisi yok';
    const fourteenText = analysis.topFourteenKaratProduct ? `${analysis.topFourteenKaratProduct.name}: ${formatCurrency(analysis.topFourteenKaratProduct.value)}` : '14 Ayar ürün verisi yok';

    try {
      const aiUrl = getCampaignAiUrl();
      if (!aiUrl) return '';
      const response = await fetch(aiUrl, {
        method: 'POST',
        headers: await getCampaignAiHeaders(),
        body: JSON.stringify({
          campaignName: safeString(campaign?.name, currentCampaignName),
          goal: safeString(campaign?.goal, campaign?.campaignDetails?.goal || 'kampanya etkisini ölçmek'),
          selectedIdeas: [],
          message: [
            'UKBA-KASA yönetici raporu için çok kısa, sade Türkçe kampanya sonuç yorumu yaz.',
            'Ana başarı ölçütü Takı Ürün kategorisinde satış artışıdır. Özellikle 14 Ayar takı ürünlerini dikkate al.',
            'En fazla 2 cümle olsun. Abartılı ifade kullanma. Sayıları tekrar etme, sonucu yorumla.',
            `Kampanya dönemi: ${analysis.periodText}. Önceki dönem: ${analysis.previousPeriodText}.`,
            `Takı Ürün farkı: ${formatCurrency(analysis.jewelryDiff)}${analysis.jewelryDiffRate === null ? '' : `, oran %${analysis.jewelryDiffRate}`}.`,
            `Takı Ürün gram farkı: ${formatGram(analysis.jewelryGramDiff)}${analysis.jewelryGramDiffRate === null ? '' : `, oran %${analysis.jewelryGramDiffRate}`}.`,
            `14 Ayar farkı: ${formatCurrency(analysis.fourteenKaratDiff)} / ${formatGram(analysis.fourteenKaratGramDiff)}${analysis.fourteenKaratDiffRate === null ? '' : `, tutar oranı %${analysis.fourteenKaratDiffRate}`}.`,
            `Şubeler: ${branchText}.`,
            `Öne çıkan Takı Ürün: ${jewelryProductText}. Öne çıkan 14 Ayar: ${fourteenText}.`
          ].join('\n')
        })
      });
      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      if (!contentType.includes('application/json')) return '';
      if (!response.ok) return '';
      const data = await response.json().catch(() => ({}));
      return safeString(data?.reply).trim();
    } catch (error) {
      return '';
    }
  };

  const handleCreateManagerReport = async () => {
    if (!hasActiveCampaign) {
      setManagerReport(null);
      setManagerReportModalOpen(true);
      return;
    }
    setManagerReportModalOpen(true);
    setManagerReport({
      title: `${currentCampaignName} - Yönetici Kampanya Raporu`,
      loading: true
    });
    const campaignSalesAnalysis = await fetchCampaignSalesAnalysis(activeCampaign);
    const aiSalesComment = await requestCampaignSalesAiComment(activeCampaign, campaignSalesAnalysis);
    const report = generateManagerReport({
      campaignName: currentCampaignName,
      rows: activeCampaignRows,
      storePerformance,
      salesData,
      engineOutput: autoInsight,
      campaign: activeCampaign,
      branchStats,
      campaignSalesAnalysis: {
        ...campaignSalesAnalysis,
        aiComment: aiSalesComment
      }
    });
    setManagerReport(report);
  };

  const branchStats = useMemo(() => {
    const activeTasks = getCampaignTaskList(activeCampaign);
    const stats = BRANCHES.map((branch) => {
      const total = activeTasks.length;
      const done = activeTasks.filter((task) => isCampaignTaskDone(staffTaskStatus, branch.slug, safeString(activeCampaign?.id), task.id)).length;
      return { ...branch, total, done, rate: total ? Math.round((done / total) * 100) : 0 };
    });
    const sorted = [...stats].sort((a, b) => b.rate - a.rate);
    return {
      stats,
      best: sorted[0]?.label || '-',
      weak: sorted[sorted.length - 1]?.label || '-'
    };
  }, [activeCampaign, staffTaskStatus]);
  const upcomingSpecialDays = useMemo(() => getUpcomingSpecialDays(new Date()), []);

  useEffect(() => {
    const completed = campaignRows.filter((r) => r.durum === 'Tamamlandı' && safeString(r.kampanyaAdi).trim());
    const map = {};
    completed.forEach((r) => {
      const key = safeString(r.kampanyaAdi).trim();
      map[key] = (map[key] || 0) + 1;
    });
    const payload = Object.entries(map).map(([name, count]) => ({ name, count, savedAt: Date.now() }));
    try {
      localStorage.setItem('campaign_success_memory_v1', JSON.stringify(payload.slice(0, 20)));
    } catch (e) {
      // ignore localStorage write errors
    }
  }, [campaignRows]);

  const reportOptions = [
    { label: 'Personel Çıktısı', action: handleCreateStaffOutput },
    { label: 'Yönetici Raporu', action: handleCreateManagerReport },
    { label: 'Yaklaşan Özel Günler', action: () => setSpecialDaysReportOpen(true) }
  ];

  return (
    <>
    <Dialog
      className="ky-modal"
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        className: 'ky-modal-paper',
        sx: {
          background: 'linear-gradient(135deg, #f8f4ea 0%, #f3efe5 45%, #ebe4d6 100%)'
        }
      }}
    >
      <DialogContent className="ky-dialog-content" sx={{ p: 1.2, overflow: 'hidden' }}>
        <Box
          className="ky-shell"
          sx={{
            border: '1px solid rgba(151,113,39,0.22)',
            borderRadius: 2.2,
            height: 'calc(100vh - 20px)',
            maxHeight: 'calc(100vh - 20px)',
            p: 1.2,
            boxShadow: '0 22px 55px rgba(87,65,25,0.16)',
            background: 'rgba(255,252,246,0.82)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            overflow: 'hidden'
          }}
        >
          <Box className="ky-header" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box className="ky-header-left" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1, pr: 1 }}>
              <Box sx={{ minWidth: 0, pt: 0.15 }}>
                <Typography sx={{ color: '#2f2516', fontWeight: 800, fontSize: '1.35rem', letterSpacing: '0.02em' }}>
                  Kampanya Yönetimi Paneli
                </Typography>
                <Typography sx={{ color: '#7a6b55', fontSize: '0.82rem', mt: 0.2 }}>
                  Planla • Uygula • Takip Et
                </Typography>
              </Box>
              <Tooltip title="Kullanım kılavuzunu aç" arrow placement="bottom">
                <IconButton
                  className="ky-guide-btn"
                  type="button"
                  onClick={() => setCampaignGuideOpen(true)}
                  aria-label="Kampanya yönetimi kullanım kılavuzu"
                  sx={{
                    flexShrink: 0,
                    mt: 0.1,
                    color: '#4a3a22',
                    border: '1px solid rgba(151,113,39,0.4)',
                    background: 'linear-gradient(165deg, #fff8e7 0%, #f0dcc0 100%)',
                    boxShadow: '0 2px 8px rgba(90,60,20,0.12)',
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: '#ffefc4', borderColor: 'rgba(120,80,20,0.55)', boxShadow: '0 4px 14px rgba(151,113,39,0.25)' }
                  }}
                >
                  <MenuBookIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="ky-header-right" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Typography
                className="ky-branch-label"
                sx={{
                  color: '#6b5b40',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.035em',
                  mr: 0.25,
                  lineHeight: 1
                }}
              >
                Şube Panelleri
              </Typography>
              {BRANCHES.map((branch) => (
                <Button
                  key={branch.slug}
                  className="ky-branch-btn"
                  size="small"
                  variant="outlined"
                  onClick={() => setActiveStaffBranch(branch.slug)}
                  sx={{
                    minHeight: 38,
                    px: 1.75,
                    py: 0.9,
                    borderRadius: 1.8,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 800,
                    color: '#3b3325',
                    borderColor: 'rgba(151,113,39,0.26)',
                    bgcolor: 'rgba(255,255,255,0.65)',
                    '&:hover': {
                      borderColor: 'rgba(151,113,39,0.52)',
                      bgcolor: 'rgba(255,248,231,0.95)',
                      boxShadow: '0 8px 18px rgba(151,113,39,0.16)'
                    }
                  }}
                >
                  🏪 {branch.label.replace(' Şube', '')}
                </Button>
              ))}
              <IconButton
                className="ky-close-btn"
                onClick={onClose}
                sx={{
                  color: '#4a3415',
                  border: '1px solid rgba(151,113,39,0.28)',
                  background: 'rgba(255,255,255,0.72)',
                  '&:hover': { background: 'rgba(255,248,231,0.95)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box className="ky-actions" sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              className="ky-action-create"
              variant="contained"
              onClick={() => {
                setAiPlanDraft((prev) => ({
                  ...prev,
                  campaignName: safeString(campaignInput, currentCampaignName) || 'Yeni Kampanya'
                }));
                setAiPlanOpen(true);
              }}
              sx={{
                height: 38,
                textTransform: 'none',
                fontSize: '0.84rem',
                fontWeight: 800,
                color: '#fff8e1',
                background: 'linear-gradient(90deg, #8a5a16 0%, #b9872f 55%, #d9aa55 100%)',
                boxShadow: '0 8px 18px rgba(151,113,39,0.18)'
              }}
            >
              Kampanya Oluştur
            </Button>
            <Button
              className="ky-action-list"
              variant="outlined"
              onClick={() => setCampaignListOpen(true)}
              sx={{ height: 38, textTransform: 'none', fontSize: '0.84rem', color: '#3b3325', borderColor: 'rgba(151,113,39,0.34)', bgcolor: 'rgba(255,255,255,0.72)' }}
            >
              Kampanyalar
            </Button>
            <Button
              className="ky-action-reports"
              variant="outlined"
              onClick={(event) => setReportMenuAnchor(event.currentTarget)}
              sx={{ height: 38, textTransform: 'none', fontSize: '0.84rem', color: '#7a4d0b', borderColor: 'rgba(151,113,39,0.36)', bgcolor: 'rgba(255,255,255,0.72)' }}
            >
              📊 Raporlar
            </Button>
            <Typography className="ky-active-label" sx={{ color: '#5f5140', fontSize: '0.78rem', fontWeight: 800 }}>
              Aktif rapor kampanyası: {hasActiveCampaign ? currentCampaignName : 'Kampanya seçilmedi'}
            </Typography>
          </Box>

          <Menu
            anchorEl={reportMenuAnchor}
            open={Boolean(reportMenuAnchor)}
            onClose={() => setReportMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 0.5,
                bgcolor: '#fffaf0',
                color: '#2f2516',
                border: '1px solid rgba(151,113,39,0.22)',
                minWidth: 210
              }
            }}
          >
            <MenuItem
              disabled
              sx={{
                opacity: '1 !important',
                color: '#7a4d0b',
                fontSize: '0.74rem',
                fontWeight: 900,
                borderBottom: '1px solid rgba(151,113,39,0.14)',
                whiteSpace: 'normal'
              }}
            >
              {hasActiveCampaign ? `Rapor kampanyası: ${currentCampaignName}` : 'Önce Kampanyalar listesinden bir kampanya seçin'}
            </MenuItem>
            {reportOptions.map((option) => (
              <MenuItem
                key={option.label}
                onClick={() => {
                  setReportMenuAnchor(null);
                  option.action();
                }}
                sx={{ fontSize: '0.78rem', '&:hover': { bgcolor: 'rgba(151,113,39,0.1)' } }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Box className="ky-main-grid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 300px' }, gap: 1, flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box className="ky-table-area" sx={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 0.65 }}>
              <CampaignTable
                campaigns={campaignList}
                activeCampaignId={activeCampaignId}
                onSelectCampaign={handleSelectCampaign}
                onOpenCampaignDetail={handleOpenCampaignDetail}
                onOpenVisualAssets={handleOpenVisualAssets}
              />
            </Box>
            <Box className="ky-sidebar-col" sx={{ display: 'grid', gap: 1, alignContent: 'start', gridAutoRows: 'max-content', minHeight: 0, overflow: 'hidden' }}>
              <Box
                className="ky-perf-box"
                sx={{
                  border: '1px solid rgba(151,113,39,0.2)',
                  borderRadius: 2,
                  background: 'linear-gradient(180deg, #fffaf0 0%, #f7efe0 100%)',
                  p: 1.15,
                  overflow: 'hidden',
                  alignSelf: 'start'
                }}
              >
                <Typography sx={{ color: '#3b2b14', fontSize: '1.08rem', fontWeight: 900 }}>Şube Performansı</Typography>
                <Typography sx={{ color: '#7a6b55', fontSize: '0.84rem', mb: 1 }}>
                  {hasActiveCampaign ? `${currentCampaignName} görevlerine göre` : 'Aktif kampanya seçin'}
                </Typography>
                {branchStats.stats.map((item) => (
                  <Box key={item.slug} sx={{ mb: 0.95 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.35 }}>
                      <Typography sx={{ color: '#2f2516', fontSize: '0.9rem', fontWeight: 800 }}>{item.label}</Typography>
                      <Typography sx={{ color: '#8a5a16', fontSize: '0.9rem', fontWeight: 900 }}>%{item.rate}</Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 99, bgcolor: 'rgba(151,113,39,0.13)', overflow: 'hidden' }}>
                      <Box sx={{ width: `${item.rate}%`, height: '100%', bgcolor: item.rate >= 70 ? '#22c55e' : item.rate >= 40 ? '#f59e0b' : '#ef4444' }} />
                    </Box>
                    <Typography sx={{ color: '#7a6b55', fontSize: '0.78rem', mt: 0.28 }}>
                      {item.done}/{item.total} görev tamamlandı
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ mt: 1, pt: 0.8, borderTop: '1px solid rgba(151,113,39,0.14)' }}>
                  <Typography sx={{ color: '#166534', fontSize: '0.86rem' }}>En iyi şube: {branchStats.best}</Typography>
                  <Typography sx={{ color: '#b91c1c', fontSize: '0.86rem', mt: 0.25 }}>En zayıf şube: {branchStats.weak}</Typography>
                </Box>
              </Box>
              <Box
                className="ky-days-box"
                sx={{
                  border: '1px solid rgba(151,113,39,0.2)',
                  borderRadius: 2,
                  background: 'linear-gradient(180deg, #fffaf0 0%, #f7efe0 100%)',
                  p: 1.15,
                  overflow: 'hidden',
                  alignSelf: 'start'
                }}
              >
                <Typography sx={{ color: '#3b2b14', fontSize: '1.08rem', fontWeight: 900, mb: 0.65 }}>
                  Yaklaşan Özel Günler
                </Typography>
                {upcomingSpecialDays.map((day) => (
                  <Tooltip
                    key={`${day.name}-${day.dateText}`}
                    arrow
                    placement="left"
                    title={
                      <Box sx={{ p: 0.4, maxWidth: 260 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, mb: 0.25 }}>{day.name}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.35 }}>{getSpecialDayCampaignIdea(day)}</Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ mb: 0.75, p: 0.55, borderRadius: 1.2, cursor: 'help', '&:hover': { bgcolor: 'rgba(151,113,39,0.08)' } }}>
                      <Typography sx={{ color: '#2f2516', fontSize: '0.88rem', fontWeight: 900 }}>
                        {day.dateText} - {day.name}
                      </Typography>
                      <Typography sx={{ color: '#7a6b55', fontSize: '0.78rem' }}>
                        {day.dayText} • {day.type}
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Dialog
        className="ky-ai-dialog"
        open={aiPlanOpen}
        onClose={() => setAiPlanOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: 'ky-ai-dialog-paper',
          sx: {
            background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
            border: '1px solid rgba(151,113,39,0.24)',
            borderRadius: 2.4,
            color: '#2f2516',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 2.6, overflowX: 'hidden' }}>
          <Typography sx={{ color: '#2f2516', fontWeight: 900, fontSize: '1.18rem', mb: 1.8 }}>
            Kampanya Planı Oluştur
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.7 }}>
            <TextField
              label="Kampanya adı"
              size="small"
              value={aiPlanDraft.campaignName}
              onChange={(event) => setAiPlanDraft((prev) => ({ ...prev, campaignName: event.target.value }))}
              sx={{
                gridColumn: '1 / -1',
                '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.88)', minHeight: 46 },
                '& .MuiInputLabel-root': { color: '#6b5b40' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.24)' }
              }}
            />
            <TextField
              label="Başlangıç tarihi"
              type="date"
              size="small"
              value={aiPlanDraft.startDate}
              onChange={(event) => setAiPlanDraft((prev) => ({ ...prev, startDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ className: 'campaign-date-input' }}
              sx={{
                '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.88)', minHeight: 46 },
                '& input': { colorScheme: 'light' },
                '& .MuiInputLabel-root': { color: '#6b5b40' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.24)' }
              }}
            />
            <TextField
              label="Bitiş tarihi"
              type="date"
              size="small"
              value={aiPlanDraft.endDate}
              onChange={(event) => setAiPlanDraft((prev) => ({ ...prev, endDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ className: 'campaign-date-input' }}
              sx={{
                '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.88)', minHeight: 46 },
                '& input': { colorScheme: 'light' },
                '& .MuiInputLabel-root': { color: '#6b5b40' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.24)' }
              }}
            />
            <TextField
              label="Kısa not / hedef"
              size="small"
              multiline
              minRows={2}
              value={aiPlanDraft.note}
              onChange={(event) => setAiPlanDraft((prev) => ({ ...prev, note: event.target.value }))}
              sx={{
                gridColumn: '1 / -1',
                '& .MuiInputBase-root': { color: '#2f2516', bgcolor: 'rgba(255,255,255,0.88)', minHeight: 86, alignItems: 'flex-start' },
                '& .MuiInputLabel-root': { color: '#6b5b40' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(151,113,39,0.24)' }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.6, py: 1.5, bgcolor: 'rgba(255,250,240,0.96)', gap: 1, borderTop: '1px solid rgba(151,113,39,0.14)' }}>
          <Button onClick={() => setAiPlanOpen(false)} sx={{ textTransform: 'none', color: '#4b5563', fontSize: '0.95rem', px: 2, py: 0.9 }}>Kapat</Button>
          <Button
            variant="contained"
            onClick={handleGenerateAiPlan}
            sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 900, bgcolor: '#9a6a1f', fontSize: '0.95rem', px: 2.2, py: 0.9, '&:hover': { bgcolor: '#7a4d0b' } }}
          >
            Planı Oluştur
          </Button>
        </DialogActions>
      </Dialog>
      <CampaignDetailTabsModal
        open={!!detailCampaign}
        campaign={detailCampaign}
        onSave={handleSaveCampaignDetail}
        onClose={() => setDetailCampaign(null)}
      />
      <CampaignVisualAssetsModal
        open={!!visualAssetsFor}
        campaign={visualAssetsFor}
        onClose={() => setVisualAssetsFor(null)}
        onSlotImageSet={handleVisualSlotImageSet}
        onAddSlot={handleAddVisualSlot}
        onSlotIdeaNoteSet={handleVisualSlotIdeaNoteSet}
        onRemoveSlot={handleRemoveExtraVisualSlot}
      />
      <CampaignListModal
        open={campaignListOpen}
        campaigns={campaignList}
        activeCampaignId={activeCampaignId}
        onClose={() => setCampaignListOpen(false)}
        onView={handleOpenCampaignDetail}
        onDelete={handleDeleteCampaign}
        onClearAll={handleClearAllCampaignData}
      />
      <StaffOutputModal open={staffOutputModalOpen} output={staffOutput} hasActiveCampaign={hasActiveCampaign} onClose={() => setStaffOutputModalOpen(false)} />
      <ManagerReportModal open={managerReportModalOpen} report={managerReport} hasActiveCampaign={hasActiveCampaign} onClose={() => setManagerReportModalOpen(false)} />
      <SpecialDaysReportModal open={specialDaysReportOpen} onClose={() => setSpecialDaysReportOpen(false)} />
      <Dialog
        className="ky-staff-dialog"
        open={!!activeStaffBranch}
        onClose={() => setActiveStaffBranch(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: 'ky-staff-dialog-paper',
          sx: {
            background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
            border: '1px solid rgba(151,113,39,0.24)',
            borderRadius: 2,
            color: '#2f2516',
            maxHeight: '92vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 1.4, overflow: 'hidden' }}>
          {activeStaffBranch ? (
            <StaffBranchCampaignPanel
              sube={activeStaffBranch}
              activeCampaign={activeCampaign}
              embedded
              onStatusChange={(nextStatus) => setStaffTaskStatus(nextStatus)}
              onCampaignUpdate={handleCampaignUpdateFromBranch}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 1.4, py: 1, borderTop: '1px solid rgba(151,113,39,0.14)', bgcolor: 'rgba(255,250,240,0.96)' }}>
          <Button onClick={() => setActiveStaffBranch(null)} sx={{ textTransform: 'none', color: '#4b5563' }}>Kapat</Button>
          <Button
            variant="contained"
            onClick={() => {
              setActiveStaffBranch(null);
            }}
            sx={{ textTransform: 'none', color: '#fff8e1', fontWeight: 900, bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

    <Dialog
      className="ky-guide-dialog"
      open={campaignGuideOpen}
      onClose={() => setCampaignGuideOpen(false)}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        className: 'ky-guide-dialog-paper',
        sx: {
          borderRadius: 2.4,
          background: 'linear-gradient(180deg, #fffaf0 0%, #f3eadb 100%)',
          border: '1px solid rgba(151,113,39,0.24)',
          color: '#2f2516',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        component="div"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1, pb: 0.5, fontSize: '1.08rem', fontWeight: 800 }}
      >
        Şube paneli — personel kullanım kılavuzu
        <IconButton
          onClick={() => setCampaignGuideOpen(false)}
          size="small"
          aria-label="Kapat"
          sx={{ color: '#4a3415' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ borderColor: 'rgba(151,113,39,0.2)', py: 1.75, maxHeight: { xs: '70vh', sm: 'calc(90vh - 120px)' }, overflowY: 'auto' }}
      >
        <Typography sx={{ color: '#3b2b14', fontSize: '0.92rem', lineHeight: 1.5, mb: 1.3 }}>
          Personel, kampanya işlemlerini <strong>Şube Panelleri</strong> bölümünden yapar. Aşağıdaki adımlar günlük kullanım için yeterlidir.
        </Typography>

        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 900, color: '#5c4a2e', mb: 0.6 }}>
          1) Şube panelini aç
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: '#3b3325', fontSize: '0.88rem', lineHeight: 1.55, mb: 1.2, '& li': { mb: 0.45 } }}>
          <li><strong>Şube Panelleri</strong> alanından kendi şubeni aç.</li>
          <li>Açılan pencerede aktif kampanyayı ve sekmeleri göreceksin.</li>
        </Box>

        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 900, color: '#5c4a2e', mb: 0.6 }}>
          2) Kampanya detaylarını incele
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: '#3b3325', fontSize: '0.88rem', lineHeight: 1.55, mb: 1.2, '& li': { mb: 0.45 } }}>
          <li><strong>Kampanya Detayları</strong> sekmesinde hedef, seçilen fikirler ve kampanya özetini oku.</li>
          <li>Gerekirse <strong>Yazdır</strong> ile düzenli sunum çıktısı al.</li>
        </Box>

        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 900, color: '#5c4a2e', mb: 0.6 }}>
          3) Görevleri tamamla
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: '#3b3325', fontSize: '0.88rem', lineHeight: 1.55, mb: 1.2, '& li': { mb: 0.45 } }}>
          <li><strong>Görevler</strong> sekmesinde tamamlanan maddeleri işaretle.</li>
          <li>İşaretlemeler şube performansına otomatik yansır.</li>
        </Box>

        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 900, color: '#5c4a2e', mb: 0.6 }}>
          4) Fikir gönder
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: '#3b3325', fontSize: '0.88rem', lineHeight: 1.55, mb: 1.2, '& li': { mb: 0.45 } }}>
          <li><strong>Fikir Sun</strong> sekmesinden kampanya için önerini yaz ve gönder.</li>
          <li>Bu sekmede kalan süreyi gösteren <strong>geri sayım sayacı</strong> bulunur.</li>
          <li>Kampanya başlangıcına son <strong>24 saat</strong> kala fikir gönderimi kapanır; sayaç sıfıra indiğinde gönderim butonu pasif olur.</li>
          <li>Fikirler yönetici onayına gider.</li>
        </Box>

        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 900, color: '#5c4a2e', mb: 0.6 }}>
          5) Görselleri kullan
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: '#3b3325', fontSize: '0.88rem', lineHeight: 1.55, '& li': { mb: 0.45 } }}>
          <li><strong>Kampanya görselleri</strong> sekmesinden görselleri görüntüle ve indir.</li>
          <li>Görsel yükleme yönetim panelinden yapılır; personel paneli indirme/görüntüleme içindir.</li>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid rgba(151,113,39,0.16)', bgcolor: 'rgba(255,250,240,0.9)' }}>
        <Button
          variant="contained"
          onClick={() => setCampaignGuideOpen(false)}
          sx={{ textTransform: 'none', fontWeight: 800, color: '#fff8e1', bgcolor: '#9a6a1f', '&:hover': { bgcolor: '#7a4d0b' } }}
        >
          Tamam
        </Button>
      </DialogActions>
    </Dialog>
    <style>{`
      @media (max-width: 600px) {
        .ky-dialog-content {
          padding: 6px !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
        }

        .ky-shell {
          height: auto !important;
          min-height: calc(100svh - 12px) !important;
          max-height: none !important;
          padding: 8px !important;
          overflow-x: hidden !important;
          overflow-y: visible !important;
          gap: 10px !important;
        }

        .ky-header {
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 8px !important;
        }

        .ky-header-left {
          padding-right: 0 !important;
        }

        .ky-header-left .MuiTypography-root:first-of-type {
          font-size: 1.1rem !important;
        }

        .ky-header-right {
          justify-content: flex-start !important;
          gap: 6px !important;
        }

        .ky-branch-label {
          width: 100% !important;
          margin-right: 0 !important;
          font-size: 0.88rem !important;
        }

        .ky-branch-btn {
          /* Uc sube tek satirda: ucuncusu tek basina satiri kapliyordu */
          flex: 1 1 calc(33.333% - 6px) !important;
          min-width: 0 !important;
          font-size: 0.78rem !important;
          padding-left: 8px !important;
          padding-right: 8px !important;
        }

        .ky-close-btn {
          margin-left: auto !important;
        }

        .ky-actions {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 6px !important;
          align-items: stretch !important;
        }

        .ky-action-create {
          grid-column: 1 / -1 !important;
          width: auto !important;
          justify-self: start !important;
          min-width: 180px !important;
          min-height: 40px !important;
        }

        .ky-action-list,
        .ky-action-reports {
          width: 100% !important;
          min-height: 38px !important;
        }

        .ky-active-label {
          grid-column: 1 / -1 !important;
          width: 100% !important;
          font-size: 0.72rem !important;
          line-height: 1.35 !important;
        }

        .ky-main-grid {
          grid-template-columns: 1fr !important;
          overflow: visible !important;
          min-height: 0 !important;
          flex: none !important;
        }

        .ky-table-area {
          overflow: visible !important;
          min-height: 280px !important;
        }

        .ky-sidebar-col {
          overflow: visible !important;
          gap: 8px !important;
        }

        .ky-perf-box,
        .ky-days-box {
          overflow: visible !important;
        }

        .ky-days-box .MuiTypography-root:first-of-type {
          word-break: break-word !important;
        }

        .ky-ai-dialog-paper,
        .ky-staff-dialog-paper,
        .ky-guide-dialog-paper {
          width: calc(100vw - 8px) !important;
          max-width: calc(100vw - 8px) !important;
          margin: 4px !important;
          max-height: calc(100svh - 8px) !important;
        }

        .ky-staff-dialog-paper {
          height: calc(100svh - 8px) !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .ky-staff-dialog .MuiDialogContent-root {
          flex: 1 !important;
          min-height: 0 !important;
          overflow-y: auto !important;
          padding: 8px !important;
        }

        .ky-ai-dialog .MuiInputBase-input,
        .ky-staff-dialog .MuiInputBase-input,
        .ky-guide-dialog .MuiInputBase-input,
        .campaign-date-input {
          font-size: 16px !important;
        }

        .ky-ai-dialog .MuiDialogActions-root,
        .ky-staff-dialog .MuiDialogActions-root,
        .ky-guide-dialog .MuiDialogActions-root {
          flex-wrap: wrap !important;
          gap: 6px !important;
        }
      }
    `}</style>
    </>
  );
}

export default CampaignModal;
